// Run-Bildschirme (Phase 4, R27): Fluss mit Flussleiste, Sturmanzeige und Tageslicht; Landtrupp wählen;
// Begegnungen, Level-Ups, Items, Aufnehmen; Chronik und Run-Übersicht. Liest den Run-Zustand und ruft
// nur die Funktionen der Run-Simulation auf, die ihn ändern.
import {
  biomeOfDay,
  combatRules,
  type Aptitude,
  type PostId,
  defaultKniff,
  fillNumbers,
  fromWhom,
  getAbility,
  getArtifact,
  getClass,
  getEncounter,
  getItem,
  itemLabel,
  itemText,
  getLevelTree,
  getSpecies,
  items as allItems,
  runRules,
  type LevelBranch,
  type LevelNode,
  type EventDef,
  type Wording,
  type EventEffect,
  fmtPct,
  traitOfEntry,
} from '../content/index.ts';
import {
  acceptRecruit,
  aptitude,
  assignPost,
  commonPlaces,
  placeOf,
  postsOf,
  setOff,
  answerHonor,
  buy,
  chooseArtifact,
  chooseLevel,
  chooseOption,
  combatProfile,
  daylight,
  declineRecruit,
  describe,
  describeStep,
  dismissReport,
  displayName,
  equipItem,
  eventOptions,
  eventText,
  foeSummary,
  fieldSlotsOf,
  gateStrength,
  stormClockOn,
  giveUpAttempt,
  hangPennant,
  honorText,
  introText,
  pennantLine,
  topMemories,
  isBossLanding,
  itemSlots,
  land,
  landingView,
  leaveShore,
  levelChoices,
  maxHp,
  maxTreeHp,
  pass,
  profileWithChoice,
  renameFriend,
  sendOnErrand,
  stageIfLanding,
  stageNow,
  startNextDay,
  tryAttempt,
  useItem,
  xpForLevel,
  xpToNext,
  STAR_WORDS,
  starTraits,
  joinWords,
  compareRuns,
  runFacts,
  runRecord,
  type RunFriend,
  type RunRecord,
  type RunState,
} from '../sim/index.ts';
import { sprite, sprites } from './art.ts';
import { rindenkarte, type CardData } from './karte.ts';
import { recordHtml } from './library.ts';
import { logbookHtml, regalHtml } from './ende.ts';
import { fastForward, followRun, needsDeparture, toggleView, travelShare, traveling, viewMode } from './fahrt/index.ts';
import { CLASS_COLORS } from './render.ts';
import { rangeWord, speedWord } from './words.ts';
export { speedWord };

export type RunApp = {
  run: RunState;
  /** Nach jeder Änderung: speichern und den passenden Bildschirm zeigen */
  sync(): void;
  toTitle(): void;
  showChronicle(): void;
};

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
const pct = (x: number) => `${Math.round(x * 100)} %`;
const fmt = (x: number, d = 1) => x.toFixed(d).replace('.', ',');
const stageName = (id: string) => combatRules.stormLevels.find((l) => l.id === id)?.name ?? id;
const signName = (id: string) => runRules.signs.find((s) => s.id === id)?.name ?? id;
/** Spieltest Phase 4: Items klar als dauerhaft oder zum Verbrauchen kennzeichnen */
const itemKind = (id: string) => (getItem(id).kind === 'consumable' ? 'Verbrauch' : 'dauerhaft');
const itemTag = (id: string) => `<span class="itag ${getItem(id).kind === 'consumable' ? 'use' : 'keep'}">${itemKind(id)}</span>`;
/** Runde 8 (Fabi): Wie selten etwas ist, gehört zur Entscheidung dazu (I22) */
const RARITY_WORD: Record<string, string> = { gewoehnlich: 'gewöhnlich', selten: 'selten', 'sehr-selten': 'sehr selten' };
const rarityTag = (r: string | undefined) => (r && r !== 'gewoehnlich' ? `<span class="rtag ${r}">${RARITY_WORD[r]}</span>` : '');
/** Spieltest Phase 4 (Fabi): Items sind auch im Text als Items erkennbar, gerahmt wie im Gepäck */
// Spieltest Phase 4 und Phase Kern (Fabi): Über jedem Item steht beim Darüberfahren, was es kann
const itemName = (id: string) => `<span class="iname ${getItem(id).kind === 'consumable' ? 'use' : 'keep'}" data-tip="${esc(itemText(id))}">${esc(itemLabel(id))}</span>`;
const ITEM_NAMES = [...allItems].sort((a, b) => b.name.length - a.name.length);
/** Text sicher machen und Item-Namen darin rahmen */
function markItems(text: string): string {
  let out = esc(text);
  const used: [string, string][] = [];
  ITEM_NAMES.forEach((i, k) => {
    const name = esc(i.name);
    if (!out.includes(name)) return;
    const key = `\u0000${k}\u0000`;
    out = out.split(name).join(key);
    used.push([key, itemName(i.id)]);
  });
  for (const [key, html] of used) out = out.split(key).join(html);
  return out;
}
/** Was gerade vorübergehend auf einem Freund wirkt */
/** I6: Was bis zum nächsten Kampf wirkt, kurz für Abzeichen und mit dem Itemtext für die Hover-Info */
const activeBoosts = (f: RunFriend): { label: string; tip: string }[] => [
  ...(f.xpBoost > 0 ? [{ label: `im nächsten Kampf +${Math.round(f.xpBoost * 100)} % XP`, tip: 'Gilt für den nächsten Kampf, den dieser Freund gewinnt.' }] : []),
  ...(f.boosts ?? []).filter((id) => getItem(id).use?.next).map((id) => ({ label: `im nächsten Kampf: ${itemLabel(id)}`, tip: itemText(id) })),
];
const activeEffects = (f: RunFriend): string[] => activeBoosts(f).map((b) => b.label);
/** Runde 6 (Fabi): In den schmalen Karten nur ein kurzes Abzeichen, was genau wirkt, steht in der Hover-Info */
const boostBadge = (f: RunFriend): string => {
  const list = activeEffects(f);
  return list.length ? ` <span class="badge soft" data-tip="${esc(list.join('\n'))}">gestärkt</span>` : '';
};

// Kreise statt Tiere: Zeichen als schlichte Symbole
const GROUP_GLYPH: Record<string, string> = { rauch: '☁', licht: '✦', zelte: '⛺', spuren: '∴', laerm: '♫', stille: '❀', glanz: '✧' };
/** Fabi (Runde 7): Jedes Zeichen hat drei Fassungen; das Bild hängt an der Art des Zeichens */
const signGlyph = (id: string): string => GROUP_GLYPH[runRules.signs.find((s) => s.id === id)?.group ?? id] ?? '?';
const CAT_GLYPH: Record<string, string> = { kampf: '⚔', befallene: '✳', haendler: '⚖', auftrag: '✉', ereignis: '❖' };
const SKY: Record<string, string> = {
  morgen: 'rgba(230, 230, 240, 0.12)',
  vormittag: 'rgba(255, 245, 220, 0.06)',
  mittag: 'rgba(255, 250, 230, 0.03)',
  nachmittag: 'rgba(255, 220, 160, 0.07)',
  abend: 'rgba(255, 170, 110, 0.12)',
  daemmerung: 'rgba(120, 80, 140, 0.22)',
  nacht: 'rgba(10, 10, 40, 0.45)',
};

/** Ui-Zustand, der nicht in den Run gehört: gewählter Landtrupp, offener Dialog */
const ui = {
  partySel: null as Set<string> | null,
  dialog: null as null | { kind: 'friend'; id: string; peek?: boolean } | { kind: 'item'; item: string } | { kind: 'levelup'; id: string } | { kind: 'naming' } | { kind: 'menu' },
  /** E40 (Fabi, Runde 8): Verschenken in zwei Schritten; welche Option gerade ihre Items zeigt */
  giftFor: null as string | null,
  /** Level-Ups, die man auf später verschoben hat (nur bis zur nächsten Änderung) */
  laterLevel: new Set<string>(),
  /** T20 (Fabi): Ab diesem Eintrag des Ereignis-Verlaufs zeigt das Fenster den neuen Schritt */
  logMark: 0,
  /** T20: welcher Schritt zuletzt neu eingeblendet wurde, damit er nur einmal sanft erscheint */
  shownStep: '',
  /** T20 (Fabi, Abschlusstest Phase 4 und Phase Kern): welche Bewerbung man schon gelesen hat; erst der Brief, dann die Entscheidung */
  letterRead: '',
  /** T32 (Fabi, Phase Kern, 17.10.2026): bei welchem Schritt einer Begegnung man „Was macht ihr?“ gedrückt hat */
  optionsShown: '',
};

/** B24: Ein Klick auf ein Tier an Bord öffnet seine Karte */
export function openFriendCard(id: string): void {
  ui.dialog = { kind: 'friend', id };
}

export function startNaming(): void {
  ui.dialog = { kind: 'naming' };
}

// ---------- Fluss ----------

function hpClass(share: number): string {
  return share < 0.34 ? 'low' : share < 0.67 ? 'mid' : '';
}

function barometer(run: RunState): string {
  // R25, P4: Vorsprung als Barometer, die Stufen farbig, die Nadel zeigt jetzt, der blasse Strich, wo ein Halt mit Kampf
  // hinführt. Spieltest Phase 4 (Fabi): ohne Erklärtext, das lernt man beim Spielen
  // R38: Mit dem Unwetter als Uhr ist der Vorsprung am Morgen der volle Ausschlag
  const max = stormClockOn() ? runRules.storm.clock.startLead : runRules.storm.startLead + 2;
  const stages = runRules.storm.stages;
  const parts: string[] = [];
  let hi = max;
  for (const st of stages) {
    const lo = st.above ?? 0;
    const w = Math.max(0, hi - lo) / max;
    parts.push(`<span class="st-${st.id}" style="width:${w * 100}%" title="${esc(stageName(st.id))}"></span>`);
    hi = lo;
  }
  const pos = (lead: number) => (1 - Math.min(max, Math.max(0, lead)) / max) * 100;
  const showGhost = run.pending.kind === 'river';
  return `<div class="barometer" title="Vorsprung vor dem Sturm">Sturm <b>${esc(stageName(stageNow(run)))}</b>
    <div class="scale">${parts.join('')}${showGhost ? `<i class="needle ghost" style="left:calc(${pos(run.lead - runRules.storm.time.land - runRules.storm.time.fight)}% - 1px)" title="Nach einem Halt mit Kampf etwa hier"></i>` : ''}<i class="needle" style="left:calc(${pos(run.lead)}% - 1px)"></i></div>
    <span>${fmt(run.lead)} Stunden Vorsprung</span></div>`;
}

function riverHead(app: RunApp): string {
  const run = app.run;
  const light = daylight(run);
  // R41 (Erste Fassung): das Biom des Tages in seiner Leitfarbe
  const biome = biomeOfDay(run.day);
  return `<div class="day">Tag ${run.day} von ${runRules.days} <span class="biome-name" style="--biome:${biome.color}">${esc(biome.name)}</span><small>${esc(light.name)}</small></div>
    ${barometer(run)}
    ${stormClockOn() && run.bossPreview?.day === run.day ? `<span class="gate-chip"${gateTip(run)}>${gateIcon(biome.id)} Durchlass <b>${Math.round(gateStrength(run) * 100)} %</b></span>` : ''}
    <span class="hint">Am Ufer Platz für <b>${fieldSlotsOf(run)}</b></span>
    <span class="spacer"></span>
    <button id="btn-chronicle">Chronik <kbd class="pad">Y</kbd></button>
    <button id="btn-menu">Menü</button>`;
}

/**
 * Spieltest Phase 4 (Fabi): Der Fluss soll fließen, nicht wie eine Liste wirken. Keine Nummern, kein Blick bis zum
 * Ende: hinter dem Floß verblassen die letzten Halte, vorn liegt die nächste Anlegestelle, dahinter Dunst. Der
 * Durchlass taucht erst auf, wenn er nah ist. Wie weit der Tag ist, zeigt das Licht.
 */
let lastStep = -1;
function riverbar(run: RunState): string {
  const n = run.landings.length;
  const landedAt = new Set(run.haltedAt ?? []);
  const boss = isBossLanding(run);
  // Spieltest Phase 4 (Fabi): Was man vom Hohen Baum oder mit dem Fernglas weiter vorn erspäht hat, sieht man, wenn man
  // den Fluss entlangscrollt; dazwischen und dahinter liegt Dunst
  const revealedAhead = (run.revealed ?? []).filter((i) => i > run.landing && i < n).map((i) => i - run.landing);
  const furthest = boss ? 0 : Math.max(runRules.visibleAhead - 1, ...revealedAhead);
  const bossAt = n - run.landing; // so viele Stellen vor uns liegt der Durchlass
  const lastPos = boss ? 56 : 56 + Math.max(furthest, bossAt <= 2 ? bossAt : 0) * 15;
  const width = Math.max(100, lastPos + 30);
  const at = (pos: number) => `${(pos / width) * 100}%`;
  const marks: string[] = [];
  // Hinter dem Floß: die letzten zwei Halte, blasser je weiter weg
  for (let back = 1; back <= 2; back++) {
    const i = run.landing - back;
    if (i < 0) continue;
    const landed = landedAt.has(i);
    marks.push(`<div class="mark past${landed ? ' landed' : ''}" style="left:${at(30 - back * 13)};opacity:${back === 1 ? 0.55 : 0.28}"><span class="pin">${landed ? '✓' : '·'}</span><span>${landed ? 'angelegt' : 'vorbei'}</span></div>`);
  }
  if (boss) {
    marks.push(`<div class="mark current boss" style="left:${at(56)}">${gatePin(biomeOfDay(run.day).id)}${gateDots(run)}<span>Der Durchlass</span></div>`);
  } else {
    for (let ahead = 0; ahead <= furthest && run.landing + ahead < n; ahead++) {
      const i = run.landing + ahead;
      const seen = ahead < runRules.visibleAhead || revealedAhead.includes(ahead);
      if (!seen) {
        marks.push(`<div class="mark ahead hazy" style="left:${at(56 + ahead * 15)}"><span class="pin">…</span><span>im Dunst</span></div>`);
        continue;
      }
      const v = landingView(run, i);
      const pin = v.truth ? CAT_GLYPH[run.landings[i]!.category] ?? '?' : signGlyph(v.sign);
      const label = v.truth ? `<span class="truth">${esc(v.truth)}</span>` : esc(signName(v.sign));
      marks.push(`<div class="mark${ahead === 0 ? ' current' : ' ahead'}" style="left:${at(56 + ahead * 15)}"><span class="pin">${pin}</span><span>${label}</span></div>`);
    }
    // Der Durchlass zeigt sich erst, wenn er nah ist. R38 (Fabi, Phase Kern): Mit dem Unwetter als Uhr sieht man
    // immer, wie viele Anlegestellen bis zum Durchlass kommen
    if (bossAt <= 2) marks.push(`<div class="mark boss distant" style="left:${at(56 + bossAt * 15)}"${gateTip(run)}>${gatePin(biomeOfDay(run.day).id)}${gateDots(run)}<span>Durchlass in Sicht</span></div>`);
    else if (stormClockOn()) marks.push(`<div class="mark boss distant" style="left:${at(lastPos + 12)}"${gateTip(run)}>${gatePin(biomeOfDay(run.day).id)}${gateDots(run)}<span>Durchlass, noch ${bossAt} Anlegestellen</span></div>`);
    else if (run.bossPreview?.day === run.day) marks.push(`<div class="mark boss distant scouted-mark" style="left:${at(lastPos + 12)}" title="${esc(`Am Durchlass wartet: ${foeSummary(run.bossPreview.enemies)}`)}">${gatePin(biomeOfDay(run.day).id)}<span>ausgekundschaftet</span></div>`);
  }
  const moving = lastStep >= 0 && run.step !== lastStep;
  lastStep = run.step;
  const water = `<svg class="water" viewBox="0 0 1000 100" preserveAspectRatio="none" aria-hidden="true">
      <path class="bed" d="M0,38 C120,22 260,58 400,44 S700,22 820,40 S960,52 1000,44 L1000,92 C880,80 760,98 600,86 S300,70 180,86 S40,96 0,90 Z"/>
      <path class="flow f1" d="M0,60 C150,50 300,74 450,62 S760,48 1000,64"/>
      <path class="flow f2" d="M0,74 C200,66 380,84 560,72 S820,64 1000,78"/>
    </svg>`;
  return `<div class="river-scroll"><div class="river-track" style="width:${width}%">${water}
      <div class="fog" style="left:${at(lastPos + 9)}"></div>
      ${stormEdge(run, at, width)}
      <div class="marks${moving ? ' moving' : ''}">${marks.join('')}<div class="boat" style="left:${at(33)}" title="Euer Floß">${pennants(run)}${sprite('ui', 'boot-marke') ? '<i class="boat-art"></i>' : '⛵'}</div></div>
    </div></div>
    ${stormSky(run)}
    <div class="daylight" style="background:${SKY[daylight(run).id]}"></div>
    <div class="dayclock">${esc(daylight(run).name)}</div>${width > 100 ? '<div class="scroll-hint">weiter flussabwärts ›</div>' : ''}`;
}

/** R25 (Fabi, Runde 7): Wie nah das Unwetter ist, sieht und spürt man auf dem Fluss: Wolken von hinten, Regen, Blitze */
const RAIN_DROPS: Record<string, number> = { ruhig: 0, aufziehend: 0, wetterleuchten: 6, nah: 26, dicht: 60, eingeholt: 90 };
let lastStage = '';
/** F38 (Lead-Auftrag): Für jeden Abschied flattert ein Wimpel am Heck; darüber liest man, wer das war */
function pennants(run: RunState): string {
  const list = (run.pennants ?? []).map((id) => run.gone.find((f) => f.id === id)).filter((f): f is RunFriend => !!f);
  if (!list.length) return '';
  const flags = list
    .map((f, i) => `<span class="pennant" style="--c:${CLASS_COLORS[f.friendClass]};--i:${i}"${tip([pennantLine(f)])}><b>${esc(f.name)}</b></span>`)
    .join('');
  return `<span class="pennants" aria-label="Wimpel für eure Gegangenen">${flags}</span>`;
}

function stormSky(run: RunState): string {
  const stage = stageNow(run);
  const order = runRules.storm.stages.map((s) => s.id);
  const worse = lastStage !== '' && order.indexOf(stage) > order.indexOf(lastStage);
  lastStage = stage;
  const level = combatRules.stormLevels.find((l) => l.id === stage);
  // Regentropfen an festen, aber unregelmäßigen Stellen, damit nichts flackert, wenn das Bild neu gezeichnet wird
  const n = RAIN_DROPS[stage] ?? 0;
  const reach = stage === 'nah' ? 0.45 : stage === 'wetterleuchten' ? 0.25 : 1;
  const drops: string[] = [];
  for (let i = 0; i < n; i++) {
    const x = ((i * 37.7) % 100) * reach;
    const delay = ((i * 0.613) % 1).toFixed(2);
    const dur = (0.45 + ((i * 0.29) % 0.35)).toFixed(2);
    drops.push(`<i style="left:${x.toFixed(1)}%;animation-delay:-${delay}s;animation-duration:${dur}s"></i>`);
  }
  return `<div class="stormwall"></div><div class="flash"></div><div class="rain">${drops.join('')}</div>
    <div class="mood${worse ? ' worse' : ''}"><b>${esc(level?.name ?? stage)}</b>${level?.mood ? ` ${esc(level.mood)}` : ''}</div>`;
}

/** E31 (Fabi, Runde 7): Was die Kundschafter am Durchlass gesehen haben */
function scouted(run: RunState): string {
  if (run.bossPreview?.day !== run.day) return '';
  // R38 (Fabi, Phase Kern): Morgens weiß man, wer am Durchlass wartet, und sieht, wie viel Kraft der Tag ihm gelassen hat
  if (stormClockOn()) return `<p class="scouted">Am Durchlass warten heute: <b>${esc(foeSummary(run.bossPreview.enemies))}</b>.</p>${gateMeter(run)}`;
  return `<p class="scouted">Die Kundschafter haben gesehen, wer am Durchlass wartet: <b>${esc(foeSummary(run.bossPreview.enemies))}</b>.</p>`;
}

/** R38: die Kraft der Sturmwesen am Durchlass als Leiste; jeder gewonnene Kampf unterwegs nimmt ihr etwas */
function gateMeter(run: RunState): string {
  const s = gateStrength(run);
  const floor = runRules.storm.clock.floor;
  const note = run.caught ? 'Das Unwetter hat euch eingeholt, sie sind wieder bei voller Kraft.' : s >= 1 - 1e-9 ? 'Jeder Kampf, den ihr unterwegs gewinnt, nimmt ihnen etwas Kraft.' : s <= floor + 1e-9 ? 'Schwächer werden sie heute nicht mehr.' : '';
  return `<div class="gate-meter" title="Kraft der Sturmwesen am Durchlass"><span class="gate-bar"><i style="width:${Math.round(s * 100)}%"></i></span><b>${Math.round(s * 100)} %</b> Kraft${note ? `<small>${esc(note)}</small>` : ''}</div>`;
}

/** Version 4: der Durchlass im Pin, mit blinzelnden Augen; ohne Bild das Zeichen ☗ */
const gatePin = (biome?: string): string => {
  // Runde 7: das Zeichen des Durchlasses im Biom des Tages (gleiches Format, nur ein anderes Bild)
  const own = biome ? sprite('ui', `durchlass-zeichen-${biome}`) : undefined;
  return sprite('ui', 'durchlass') ? `<span class="pin gate-art"><i${own ? ` style="background-image:url(art/${own.file})"` : ''}></i></span>` : '<span class="pin">☗</span>';
};
/** Runde 7: das kleine Zeichen des Durchlasses (18 × 18, ruhig) statt ☗, etwa im Kopf und über dem Durchlass */
const gateIcon = (biome: string): string => {
  const sp = sprite('ui', `durchlass-zeichen-${biome}`) ?? sprite('ui', 'durchlass');
  return sp ? `<i class="gate-ico" style="background-image:url(art/${sp.file})" aria-hidden="true"></i>` : '☗';
};

/**
 * R38, Version 4: die Kraft des Durchlasses als Punkte unter dem Pin; jeder gewonnene Kampf nimmt einen, bis zur
 * Grenze. Geht ein Punkt verloren oder kommt einer wieder, spielt er einmal sein Bild
 */
let lastDots: { day: number; full: number } | null = null;
function gateDots(run: RunState): string {
  if (!stormClockOn() || !sprite('ui', 'durchlass-staerke') || run.bossPreview?.day !== run.day) return '';
  const c = runRules.storm.clock;
  const total = Math.max(1, Math.round((1 - c.floor) / c.weakenPerWin));
  const full = Math.max(0, Math.min(total, Math.round((gateStrength(run) - c.floor) / c.weakenPerWin)));
  const before = lastDots?.day === run.day ? lastDots.full : full;
  lastDots = { day: run.day, full };
  const dots = Array.from({ length: total }, (_, i) => {
    const anim = i >= full && i < before ? ' lose' : i < full && i >= before ? ' gain' : '';
    return `<i class="dot${i < full ? '' : ' empty'}${anim}" style="--n:${anim ? Math.abs(i - (anim === ' lose' ? before - 1 : before)) : 0}"></i>`;
  });
  return `<span class="gate-dots" aria-label="Kraft des Durchlasses">${dots.join('')}</span>`;
}

/**
 * Version 4: die Wolkenkante des Unwetters auf dem Fluss, dort, wo das Unwetter ist: je Halt, den es noch entfernt ist,
 * ein Abstand zwischen zwei Anlegestellen hinter dem Floß; holt es euch ein, zieht sie über das Floß hinweg
 */
function stormEdge(run: RunState, at: (pos: number) => string, width: number): string {
  if (!sprite('ui', 'sturmkante')) return '';
  const t = runRules.storm.time;
  const haltsAway = Math.max(0, run.lead) / (t.land + t.fight);
  const right = run.lead <= 1e-9 ? width + 12 : Math.max(-4, 33 - haltsAway * 15);
  return `<div class="storm-art" style="left:calc(${at(right)} - 100%)"></div>`;
}

/** R38: beim Darüberfahren über den Durchlass auf dem Fluss: wer dort wartet, mit wie viel Kraft */
function gateTip(run: RunState): string {
  if (!stormClockOn() || run.bossPreview?.day !== run.day) return '';
  return ` title="${esc(`Am Durchlass warten: ${foeSummary(run.bossPreview.enemies)}. Noch ${Math.round(gateStrength(run) * 100)} % ihrer Kraft.`)}"`;
}

/** B24 (Erste Fassung): Unterwegs zwischen zwei Anlegestellen; vorn zeigt sich schon, was kommt */
function travelPanel(run: RunState): string {
  const next = isBossLanding(run) ? `<b>${esc(biomeOfDay(run.day).gate.name)}</b>` : `<b>${esc(signName(landingView(run, run.landing).sign))}</b>`;
  const left = run.landings.length - run.landing;
  return `<div class="travel-card"><h2>Ihr treibt flussabwärts</h2>
    <p>Voraus: ${next}${!isBossLanding(run) ? ` <span class="hint">(bis zum Durchlass noch ${left} Anlegestelle${left === 1 ? '' : 'n'})</span>` : ''}</p>
    <div class="eta"><i style="width:${Math.round(travelShare() * 100)}%"></i></div>
    <div class="landing-actions"><button id="btn-fast" class="primary">Vorspulen ⏩ <kbd class="pad">A</kbd></button></div></div>`;
}

/** B25, S12: Zeichen und Namen der Posten an Bord */
export const POST_INFO: Record<PostId | 'kajuete', { icon: string; name: string }> = {
  kaeptn: { icon: '⚓', name: 'Käpt\'n' },
  ausguck: { icon: '🔭', name: 'Ausguck' },
  gemeinschaft: { icon: '☕', name: 'Gemeinschaftsraum' },
  heiler: { icon: '✚', name: 'Heiler' },
  kajuete: { icon: '☾', name: 'Kajüte' },
};

/** Runde 7: Zeichen der Posten vom Grafik-Lead (posten-*, 20 × 20); fehlt es, bleibt das Schriftzeichen */
const POST_ART: Record<PostId | 'kajuete', string> = { kaeptn: 'posten-kaeptn', ausguck: 'posten-ausguck', gemeinschaft: 'posten-gemeinschaft', heiler: 'posten-heiler', kajuete: 'posten-koje' };
export function postIcon(post: PostId | 'kajuete', small = false): string {
  const sp = sprite('ui', POST_ART[post]);
  return sp ? `<i class="post-art${small ? ' small' : ''}" style="background-image:url(art/${sp.file})" aria-hidden="true"></i>` : POST_INFO[post].icon;
}
const APT_WORD: Record<Aptitude, string> = { gut: 'gut', normal: 'geht', schlecht: 'schlecht' };

/** S12: Was ein Posten mit dieser Eignung bewirkt, in einem Satz */
function postEffect(run: RunState, post: PostId, who: RunFriend | undefined): string {
  const P = runRules.posts;
  const a = who ? aptitude(who.species, post) : null;
  switch (post) {
    case 'kaeptn':
      return a ? `bekommt ${P.kaeptnXp[a]} Erfahrung je Strecke, erholt sich langsamer` : 'Wer steuert, bekommt Erfahrung und erholt sich langsamer.';
    case 'ausguck': {
      const eye = a ? P.ausguckSight[a] : 0;
      return !a ? 'Ohne Ausguck sieht man nur die Zeichen am Ufer.' : eye >= 2 ? 'sieht, was an der nächsten Anlegestelle wartet' : eye === 1 ? 'sieht, ob an der nächsten Anlegestelle gekämpft wird' : 'sieht kaum etwas';
    }
    case 'gemeinschaft':
      return `Bis zu ${commonPlaces(run)}. Wer die Schicht zusammen hier verbringt, kämpft im nächsten Kampf nebeneinander besser.`;
    case 'heiler':
      return a ? `verarztet die in der Kajüte, ${Math.round(P.heilerCare[a] * 100)} % je Strecke, erholt sich selbst langsamer` : 'Ein Heiler verarztet, wer in der Kajüte liegt.';
  }
}

/** B25 (Fabi, 29.10.2026): Zwischen zwei Schichten, nach einem Halt oder am Morgen, wird zugeteilt, dann legt das Boot ab */
function departPanel(run: RunState): string {
  const p = postsOf(run);
  const crew = run.crew.filter((f) => !f.away);
  const byId = (id: string | null) => (id ? crew.find((f) => f.id === id) : undefined);
  const chip = (post: PostId, f: RunFriend, on: boolean) => {
    const a = aptitude(f.species, post);
    return `<button class="chip apt-${a}${on ? ' on' : ''}" data-post="${post}" data-who="${f.id}" title="${esc(`${f.name} ist als ${POST_INFO[post].name} ${a === 'normal' ? 'ganz ordentlich' : a}`)}">${esc(f.name)} <i>${APT_WORD[a]}</i></button>`;
  };
  const row = (post: PostId) => {
    const on = (f: RunFriend) => (post === 'gemeinschaft' ? p.gemeinschaft.includes(f.id) : p[post] === f.id);
    const who = post === 'gemeinschaft' ? undefined : byId(p[post] as string | null);
    return `<div class="post-row"><div class="post-head"><span class="post-icon">${postIcon(post)}</span><b>${POST_INFO[post].name}</b></div>
      <div class="post-chips">${crew.map((f) => chip(post, f, on(f))).join('')}</div><div class="post-effect">${esc(postEffect(run, post, who))}</div></div>`;
  };
  const resting = p.kajuete.map((id) => byId(id)?.name).filter(Boolean);
  const morning = run.landing === 0 && run.haltsToday === 0;
  return `<div class="depart-head"><h2>${morning ? 'Der Tag beginnt' : 'Zurück an Bord'}</h2><button id="btn-setoff" class="primary">Ablegen <kbd class="pad">Start</kbd></button></div>
    <p>${morning ? 'Wer übernimmt welchen Posten, bevor ihr ablegt?' : 'Eine neue Schicht beginnt. Wer übernimmt welchen Posten?'}</p>
    <div class="posts">${(['kaeptn', 'ausguck', 'gemeinschaft', 'heiler'] as PostId[]).map(row).join('')}
      <div class="post-row kajuete"><div class="post-head"><span class="post-icon">${postIcon('kajuete')}</span><b>Kajüte</b></div><div class="post-chips">${resting.length ? `${esc(resting.join(', '))} ${resting.length === 1 ? 'ruht' : 'ruhen'} sich aus` : 'Alle haben einen Posten, niemand ruht sich aus.'}</div></div>
    </div>`;
}

function landingPanel(run: RunState): string {
  const slots = fieldSlotsOf(run);
  // B24, B25: unterwegs nur die Fahrt; nach einem Halt erst der Schichtwechsel, dann geht es los
  if (!ui.partySel && run.pending.kind === 'river' && traveling()) return travelPanel(run);
  if (!ui.partySel && needsDeparture(run)) return departPanel(run);
  if (ui.partySel) {
    const n = ui.partySel.size;
    const boss = isBossLanding(run);
    // Spieltest Phase 4: beim Wählen jeden Freund genauer ansehen können
    const picks = run.crew.map((f) => pickCard(run, f)).join('');
    // Fabi (Phase Kern, 17.10.2026 nachts): KISS. Was man beim Spielen lernt, steht hier nicht mehr
    return `<h2>Landtrupp wählen</h2>
      <p>Wer setzt über? Höchstens <b>${slots}</b>, gewählt: <b>${n}</b>.</p>
      <p class="hint">Wer an Bord bleibt, ruht sich aus.</p>
      ${boss ? scouted(run) : ''}
      <div class="pick-grid">${picks}</div>
      <div class="landing-actions">
        <button id="btn-go" class="primary" ${n === 0 || n > slots ? 'disabled' : ''}>Übersetzen <kbd class="pad">Start</kbd></button>
        <button id="btn-cancel">Abbrechen <kbd class="pad">B</kbd></button>
      </div>`;
  }
  if (isBossLanding(run)) {
    return `<h2><span class="sign-big">${gateIcon(biomeOfDay(run.day).id)}</span>Der Durchlass</h2>
      <p>${stormClockOn() ? (run.caught ? 'Das Unwetter hat euch hierher getrieben. Gekämpft wird im Unwetter, dahinter beginnt die Nacht.' : 'Hier wartet der Boss, dahinter beginnt die Nacht.') : 'Hier wartet der Boss, dahinter beginnt die Nacht. Vorbei geht es nicht. Verliert ihr, kostet ein neuer Versuch wieder Zeit, und der Boss ist wieder heil.'}</p>
      ${scouted(run)}
      <p class="hint">Am Durchlass ist Platz für <b>${slots}</b>.</p>
      <div class="landing-actions"><button id="btn-land" class="primary">Landtrupp wählen <kbd class="pad">A</kbd></button></div>`;
  }
  const v = landingView(run, run.landing);
  // Spieltest Phase 4 (Fabi): weniger ist mehr. Das Zeichen erzählt, was einen manchmal erwartet; wie lange ein Halt
  // dauert und wie nah der Sturm dann ist, zeigt das Barometer und lernt man beim Spielen
  // B25, S12: Der Ausguck sieht je nach Spezies mehr oder weniger
  const lookoutFox = v.lookout ? run.crew.find((f) => f.name === v.lookout && getSpecies(f.species).passive === 'schlau') : undefined;
  const slyEye = lookoutFox && v.truth === 'Hinterhalt' ? slyIcon() : '';
  const truth = v.truth ? `<p class="ok">${slyEye}${v.lookout ? `${esc(v.lookout)} ruft aus dem Ausguck: Dort wartet <b>${esc(v.truth)}</b>!` : `Ihr wisst, was dort wartet: <b>${esc(v.truth)}</b>.`}</p>` : '';
  const hint = v.hint && v.lookout ? `<p class="ok">${esc(v.lookout)} ruft aus dem Ausguck: ${v.hint === 'kampf' ? '„Da drüben bewegt sich etwas, ich glaube, dort wird gekämpft!“' : '„Sieht friedlich aus, Sturmwesen sehe ich keine.“'}</p>` : '';
  // R38: Mit dem Unwetter als Uhr warnt das Barometer; holt es euch ein, geht es gleich zum Durchlass
  const caught = stormClockOn()
    ? stageIfLanding(run, true) === 'eingeholt' ? '<p class="warn">Legt ihr hier an und kämpft, holt euch das Unwetter ein, und es treibt euch gleich zum Durchlass.</p>' : ''
    : stageIfLanding(run, false) === 'eingeholt' ? '<p class="warn">Der Sturm hat euch eingeholt. Wer jetzt anlegt, muss kämpfen.</p>' : '';
  // R38 (Fabi, Phase Kern): Morgens erfahrt ihr, was am Durchlass wartet; an der ersten Anlegestelle des Tages steht es hier
  const morning = stormClockOn() && run.landing === 0 && run.haltsToday === 0 ? scouted(run) : '';
  return `<h2><span class="sign-big">${signGlyph(v.sign)}</span>${esc(signName(v.sign))}</h2>
    ${v.truth ? truth : paras(runRules.signs.find((s) => s.id === v.sign)?.text ?? '') + hint}
    ${caught}
    ${morning}
    <div class="landing-actions">
      <button id="btn-land" class="primary">Anlegen <kbd class="pad">A</kbd></button>
      <button id="btn-pass">Vorbeitreiben</button>
    </div>`;
}

/** E4 (Fabi, Spieltest Phase 4): Ein Auftrag zählt Anlegestellen, an denen ihr vorbeikommt, ob ihr anlegt oder nicht */
function awayText(n: number): string {
  return n <= 1 ? 'ist nach der nächsten Anlegestelle zurück' : `ist nach ${n === 2 ? 'zwei' : n} Anlegestellen zurück`;
}
function awayBadge(f: RunFriend): string {
  return (f.away ? ` <span class="badge grey">unterwegs, ${awayText(f.away.haltsLeft)}</span>` : '') + woundBadge(f);
}

/** F42 (Fabi, Phase Kern): Die Wunde sieht man auf jeder Karte, mit ihrer Folge beim Darüberfahren */
function woundBadge(f: RunFriend): string {
  if (!f.wound) return '';
  const by = f.wound.by ? ` ${fromWhom(f.wound.by)}` : '';
  return ` <span class="badge wound" title="${esc(`${f.name} wurde${by} verwundet. Fällt ${f.name} heute noch einmal, nimmt ${f.name} Abschied. Die Wunde heilt über Nacht.`)}">✚ verwundet</span>`;
}

/** Spieltest Phase 4 (Fabi): Wie viel bis zum nächsten Level fehlt, beeinflusst, wen man mitnimmt */
function xpRow(f: RunFriend): string {
  if (f.level >= runRules.xp.maxLevel) return '<span class="row">XP <span class="hint">höchstes Level</span></span>';
  const need = xpToNext(f.level);
  const into = Math.max(0, f.xp - xpForLevel(f.level));
  return `<span class="row">XP <span class="meter xp"><span style="width:${Math.min(100, (into / need) * 100)}%"></span></span>noch ${Math.max(0, need - into)} bis Level ${f.level + 1}</span>`;
}

/** Spieltest Phase 4: Der Fluss ist die Hauptsache, die Freunde stehen schmal am Rand */
/** B25: Wo an Bord er gerade ist, als kleines Zeichen */
function postBadge(run: RunState, f: RunFriend): string {
  const place = placeOf(run, f.id);
  if (place === 'unterwegs') return '';
  return `<span class="post-badge" title="${esc(POST_INFO[place].name)}">${postIcon(place, true)}</span>`;
}

function miniCard(run: RunState, f: RunFriend): string {
  const share = f.hp / maxHp(f, run);
  const sel = ui.partySel?.has(f.id);
  const sp = getSpecies(f.species);
  return `<button class="card mini${f.away ? ' away' : ''}" data-friend="${f.id}">
    <span class="top"><span class="dot" style="background:${CLASS_COLORS[f.friendClass]}"></span><b>${esc(f.name)}</b><small>${esc(sp.name)} · ${esc(getClass(f.friendClass).name)}</small><span class="lvl">${postBadge(run, f)}Lvl ${f.level}</span></span>
    <span class="row"><span class="meter hp ${hpClass(share)}"><span style="width:${share * 100}%"></span></span>${pct(share)}</span>
    ${f.pendingLevels > 0 || f.away || f.wound || sel || activeEffects(f).length ? `<span class="row">${f.pendingLevels > 0 ? '<span class="badge">Level-Up!</span>' : ''}${awayBadge(f)}${sel ? ' <span class="badge">Landtrupp</span>' : ''}${boostBadge(f)}</span>` : ''}
  </button>`;
}

/** Landtrupp wählen: ganze Karte zum An- und Abwählen, daneben „Ansehen“ */
function pickCard(run: RunState, f: RunFriend): string {
  const max = maxHp(f, run);
  const share = f.hp / max;
  const tree = maxTreeHp(f, run);
  const sel = ui.partySel?.has(f.id);
  const sp = getSpecies(f.species);
  const prof = combatProfile(f, run);
  const kniff = getAbility(prof.kniff ?? defaultKniff(sp.classes.find((c) => c.friendClass === f.friendClass)!)!);
  return `<div class="pick${sel ? ' selected' : ''}">
    <button class="card${sel ? ' selected' : ''}${f.away ? ' away' : ''}" data-pick="${f.id}" ${f.away ? 'disabled' : ''}>
      <span class="top"><span class="dot" style="background:${CLASS_COLORS[f.friendClass]}"></span><b>${esc(f.name)}</b><small>${esc(sp.name)} · ${esc(getClass(f.friendClass).name)}</small><span class="lvl">Lvl ${f.level}</span></span>
      ${f.titles.length ? `<span class="titles">${esc(f.titles[f.titles.length - 1]!)}</span>` : ''}
      <span class="row">Leben <span class="meter hp ${hpClass(share)}"><span style="width:${share * 100}%"></span></span>${Math.round(f.hp)} / ${Math.round(max)}</span>
      ${xpRow(f)}
      ${tree !== null ? `<span class="row">Baum <span class="meter tree"><span style="width:${((f.treeHp ?? tree) / tree) * 100}%"></span></span>${pct((f.treeHp ?? tree) / tree)}</span>` : ''}
      <span class="row">${esc(getAbility(sp.passive).name)} · ${esc(kniff.name)}${f.items.length ? ` · ${f.items.map((i) => itemName(i)).join(', ')}` : ''}</span>
      <span class="row">${f.pendingLevels > 0 ? '<span class="badge">Level-Up!</span>' : ''}${awayBadge(f)}${sel ? ' <span class="badge">Landtrupp</span>' : ''}${boostBadge(f)}</span>
    </button>
    <button class="look" data-look="${f.id}" title="Genauer ansehen">Ansehen</button>
  </div>`;
}

export function renderRiver(app: RunApp): void {
  const run = app.run;
  // B24: Die Fahrt holt den Run sichtbar ein; solange sie nicht da ist, steht die alte Flussleiste
  followRun(run);
  $('river-head').innerHTML = riverHead(app);
  $('riverbar').innerHTML = riverbar(run);
  const sign = document.getElementById('fahrt-sign');
  if (sign) sign.innerHTML = fahrtSign(run);
  const viewBtn = document.getElementById('fahrt-view');
  if (viewBtn) viewBtn.textContent = viewMode() === 'quer' ? 'Von außen' : 'Ins Boot schauen';
  const fastBtn = document.getElementById('fahrt-fast');
  if (fastBtn) fastBtn.hidden = !traveling();
  // Version 4 des Manifests: Bilder für die Flussleiste (Unwetterkante, Durchlass, Floß), sobald sie geladen sind
  $('riverbar').className = `river-scene storm-${stageNow(run)}${sprite('ui', 'sturmkante') ? ' art' : ''}`;
  $('landing-panel').innerHTML = landingPanel(run);
  $('crew-count').textContent = `${run.crew.length} von ${run.crewSlots} Plätzen`;
  // Spieltest Phase 4 (Fabi): Beim Wählen des Landtrupps steht rechts nur, wer an Bord bleibt
  const aboard = ui.partySel ? run.crew.filter((f) => !ui.partySel!.has(f.id) && !f.away) : run.crew;
  $('crew').innerHTML = aboard.length ? aboard.map((f) => miniCard(run, f)).join('') : '<span class="empty">Alle setzen mit über.</span>';
  // Fabi (Phase Kern, 17.10.2026): Das Holz steht im Inventar, mit Rahmen, damit es auffällt
  $('wood').textContent = `${run.wood} Holz`;
  $('inventory').innerHTML = run.inventory.length
    ? run.inventory.map((id, i) => `<button class="item" data-item="${esc(id)}" data-idx="${i}" data-tip="${esc(itemText(id))}">${esc(itemLabel(id))} ${itemTag(id)}</button>`).join('')
    : '<span class="empty">Leer. Items gibt es beim Händler, von Gegnern und bei Ereignissen.</span>';
  $('artifacts').innerHTML = run.artifacts.length
    ? run.artifacts.map((id) => `<span class="item" title="${esc(getArtifact(id).text)}">${esc(getArtifact(id).name)}</span>`).join('')
    : '<span class="empty">Noch keins. Bosse lassen eins aus zweien wählen.</span>';
  bindRiver(app);
}

/** Oben links auf der Fahrt: was voraus liegt, kurz */
function fahrtSign(run: RunState): string {
  if (run.pending.kind !== 'river') return '';
  if (isBossLanding(run)) return `Voraus: <b>der Durchlass</b>, ${esc(biomeOfDay(run.day).gate.short)}`;
  if (needsDeparture(run)) return '';
  const v = landingView(run, run.landing);
  return `${traveling() ? 'Voraus' : 'Am Ufer'}: <b>${signGlyph(v.sign)} ${esc(v.truth ?? signName(v.sign))}</b>`;
}

function bindRiver(app: RunApp): void {
  const run = app.run;
  // B25: Zuteilen per Klick; noch einmal auf denselben schickt ihn in die Kajüte
  for (const el of document.querySelectorAll<HTMLElement>('#landing-panel [data-post]')) {
    el.onclick = () => {
      const post = el.dataset.post as PostId;
      const id = el.dataset.who!;
      assignPost(run, id, el.classList.contains('on') ? 'kajuete' : post);
      app.sync();
      restoreFocus(`[data-post="${post}"][data-who="${id}"]`);
    };
  }
  const setoff = document.getElementById('btn-setoff');
  if (setoff)
    setoff.onclick = () => {
      setOff(run);
      app.sync();
    };
  const fast = document.getElementById('btn-fast');
  if (fast) fast.onclick = () => fastForward();
  const fast2 = document.getElementById('fahrt-fast');
  if (fast2) fast2.onclick = () => fastForward();
  const viewBtn = document.getElementById('fahrt-view');
  if (viewBtn)
    viewBtn.onclick = () => {
      toggleView();
      viewBtn.textContent = viewMode() === 'quer' ? 'Von außen' : 'Ins Boot schauen';
    };
  $('btn-chronicle').onclick = () => app.showChronicle();
  $('btn-menu').onclick = () => {
    ui.dialog = { kind: 'menu' };
    app.sync();
  };
  const landBtn = document.getElementById('btn-land');
  if (landBtn) landBtn.onclick = () => startParty(app);
  const passBtn = document.getElementById('btn-pass');
  if (passBtn)
    passBtn.onclick = () => {
      pass(run);
      app.sync();
    };
  const go = document.getElementById('btn-go');
  if (go) go.onclick = () => confirmParty(app);
  const cancel = document.getElementById('btn-cancel');
  if (cancel)
    cancel.onclick = () => {
      ui.partySel = null;
      app.sync();
    };
  const toggle = (id: string, focus: string) => {
    if (!ui.partySel) return;
    if (ui.partySel.has(id)) ui.partySel.delete(id);
    else if (ui.partySel.size < fieldSlotsOf(run)) ui.partySel.add(id);
    app.sync();
    restoreFocus(focus);
  };
  for (const el of document.querySelectorAll<HTMLElement>('#crew [data-friend]')) {
    el.onclick = () => {
      const id = el.dataset.friend!;
      if (ui.partySel) return toggle(id, `[data-friend="${id}"]`);
      ui.dialog = { kind: 'friend', id };
      app.sync();
    };
  }
  for (const el of document.querySelectorAll<HTMLElement>('#landing-panel [data-pick]')) {
    el.onclick = () => toggle(el.dataset.pick!, `[data-pick="${el.dataset.pick!}"]`);
  }
  for (const el of document.querySelectorAll<HTMLElement>('#landing-panel [data-look]')) {
    el.onclick = () => {
      ui.dialog = { kind: 'friend', id: el.dataset.look! };
      app.sync();
    };
  }
  for (const el of document.querySelectorAll<HTMLElement>('#inventory [data-item]')) {
    el.onclick = () => {
      ui.dialog = { kind: 'item', item: el.dataset.item! };
      app.sync();
    };
  }
}

function restoreFocus(selector: string): void {
  (document.querySelector(selector) as HTMLElement | null)?.focus();
}

/** R27: Landtrupp vor der Überfahrt. Spieltest Phase 4 (Fabi): keine Vorauswahl, man wählt jeden selbst */
function startParty(app: RunApp): void {
  ui.partySel = new Set();
  app.sync();
  (document.querySelector('[data-pick]:not([disabled])') as HTMLElement | null)?.focus();
}

function confirmParty(app: RunApp): void {
  if (!ui.partySel || ui.partySel.size === 0) return;
  const party = [...ui.partySel];
  ui.partySel = null;
  land(app.run, party);
  app.sync();
}

/** Für die Controller-Belegung im Fluss */
export function riverAction(app: RunApp, a: string): boolean {
  if (ui.dialog || app.run.pending.kind !== 'river') return false;
  if (a === 'back' && ui.partySel) {
    ui.partySel = null;
    app.sync();
    return true;
  }
  if (a === 'start' && ui.partySel) {
    confirmParty(app);
    return true;
  }
  // B25: Start legt ab, wenn die Schicht wechselt
  if (a === 'start' && needsDeparture(app.run)) {
    setOff(app.run);
    app.sync();
    return true;
  }
  if (a === 'reroll') {
    app.showChronicle();
    return true;
  }
  return false;
}

// ---------- Dialoge ----------

export function dialogHtml(app: RunApp): string | null {
  const run = app.run;
  const p = run.pending;
  // Spieltest Phase 4 (Fabi): Während einer Aufnahme kurz einen Freund ansehen, dann zurück
  if (ui.dialog?.kind === 'friend' && ui.dialog.peek) return friendDialog(run, ui.dialog.id);
  // F38 (Lead-Auftrag): Ein Abschied bekommt seinen eigenen Moment, vor allem anderen
  if (p.kind !== 'fight' && p.kind !== 'end' && run.farewells?.length) return farewellDialog(run);
  // E4 (Spieltest Phase 4): Wer vom Auftrag zurück ist, erzählt zuerst
  if ((p.kind === 'river' || p.kind === 'recruit') && run.reports?.length) return reportDialog(run);
  // Reihenfolge: erst die Begegnung, dann Titel, Level-Ups, eigene Dialoge
  switch (p.kind) {
    case 'event':
      return eventDialog(run);
    case 'trader':
      return traderDialog(run);
    case 'errand':
      return errandDialog(run);
    case 'recruit':
      return recruitDialog(run);
    case 'bossReward':
      return bossRewardDialog(run);
    case 'night':
      return nightDialog(run);
    default:
      break;
  }
  if (ui.dialog?.kind === 'naming') return namingDialog(run);
  if (run.honor) return honorDialog(run);
  if (ui.dialog?.kind === 'friend') return friendDialog(run, ui.dialog.id);
  if (ui.dialog?.kind === 'item') return itemDialog(run, ui.dialog.item);
  if (ui.dialog?.kind === 'menu') return menuDialog();
  const up = ui.dialog?.kind === 'levelup' ? run.crew.find((f) => f.id === (ui.dialog as { id: string }).id) : run.crew.find((f) => f.pendingLevels > 0 && levelChoices(f).length > 0 && !ui.laterLevel.has(f.id));
  if (up && up.pendingLevels > 0 && levelChoices(up).length > 0) return levelDialog(run, up);
  return null;
}

/**
 * T36 (Fabi, Phase Kern, 17.10.2026 nachts): Absätze statt Textblock. Ein Zeilenumbruch in den Daten ist ein neuer
 * Absatz; mehr als drei Absätze gehören nicht in ein Fenster (außer bei Briefen, die erzählen die Welt)
 */
export const paras = (text: string): string =>
  text
    .split('\n')
    .filter((t) => t.trim())
    .map((t) => `<p>${esc(t)}</p>`)
    .join('');

/** Die Option eines Ereignisses, mit der ein Tier an Bord kommt */
/** E24: Wie gut jemand das kann, in Worten statt Prozent (Fabi) */

/** Der laufende Versuch eines Ereignisses (Verheddert, Ratespiel) */
function attemptDef(run: RunState) {
  const p = run.pending;
  if (p.kind !== 'event' || !p.optionId) return undefined;
  const def = getEncounter(p.eventId);
  const opts = (p.variant !== undefined ? def.variants![p.variant]!.options : def.options) ?? [];
  return opts.find((o) => o.id === p.optionId)?.attempt;
}

function recruitOption(run: RunState) {
  const p = run.pending;
  if (p.kind !== 'event') return undefined;
  const def = getEncounter(p.eventId);
  const opts = (p.variant !== undefined ? def.variants![p.variant]!.options : def.options) ?? [];
  return opts.find((o) => o.effects?.some((e) => e.recruit));
}

/** Welche Bewerbung gerade offen ist, damit „schon gelesen“ nur für genau diesen Brief gilt */
const letterKey = (p: { eventId: string; subject?: RunFriend }) => `${p.eventId}:${p.subject?.id ?? ''}`;

/**
 * E28 (Fabi, Spieltest Phase 4): Eine Bewerbung ist eine Karte mit Brief, dem Tier mit Klasse und Werten, der eigenen
 * Crew zum Vergleichen, Name und Entscheidung; kein zweites Nachfragen (§18).
 * T20 (Fabi, Abschlusstest Phase 4: „viel zu überladen, obwohl alles seinen Platz hat“; Ja in der Phase Kern): Vorher
 * steht der Brief allein da, als eigener Moment. Entschieden wird erst danach, und nur einmal.
 */
function applicationDialog(run: RunState): string {
  const p = run.pending;
  if (p.kind !== 'event' || !p.subject) return '';
  const def = getEncounter(p.eventId);
  const c = p.subject;
  const yes = recruitOption(run)!;
  const no = eventOptions(run).filter((o) => o.id !== yes.id && !o.hidden);
  const full = run.crew.length >= run.crewSlots;
  const lines = p.letter ? p.letter.split('\n').map(esc).join('<br>') : '';
  if (p.letter && ui.letterRead !== letterKey(p)) {
    return `<h2>${esc(def.name)}</h2>${paras(eventText(run))}
      <blockquote class="letter step-in">${lines}</blockquote>
      <div class="options"><button class="primary" data-letter-read="">Weiter</button></div>`;
  }
  const letter = p.letter ? `<details class="letter-again"><summary>Den Brief noch einmal lesen</summary><blockquote class="letter">${lines}</blockquote></details>` : '';
  const release = full
    ? `<p class="warn">Alle ${run.crewSlots} Plätze sind belegt. Wer bleibt am Ufer zurück, damit ${esc(c.name)} Platz hat?</p>
       <div class="options">${releaseButtons(run, 'data-welcome')}</div>`
    : '';
  // Q57: links die Karte, rechts Brief, Name und Entscheidung, damit alles auf einen Bildschirm passt
  return `<div class="fd-head"><div><h2>${esc(def.name)}</h2></div>${classBadge(c)}</div>
    <div class="level-layout friend-layout">
      <div class="level-who">${friendCard(run, c)}</div>
      <div class="friend-side">
        ${letter}
        ${nameField(c)}
        ${crewOverview(run, c)}
        ${release}
        <div class="options">${full ? '' : `<button class="primary" data-welcome="">${esc(yes.text)}</button>`}${no.map((o) => `<button data-opt="${esc(o.id)}">${esc(o.text)}</button>`).join('')}</div>
      </div>
    </div>`;
}

function nameField(c: RunFriend): string {
  return `<p><label>Wie soll ${esc(getSpecies(c.species).article)} ${esc(getSpecies(c.species).name)} heißen? <input id="recruit-name" class="name-input" value="${esc(c.name)}" maxlength="24" /></label></p>`;
}

/**
 * E43 (Runde 7): das Bild eines Ereignisses (ereignis-<id>, 160 × 100, zweifach) zwischen Überschrift und Text, solange
 * es offen ist; sein Rand läuft weich ins Papier. Ohne Bild steht dort nichts
 */
function eventPicture(eventId: string): string {
  const sp = sprites().find((x) => x.kind === 'ui' && (x as { event?: string }).event === eventId) ?? sprite('ui', `ereignis-${eventId}`);
  return sp ? `<div class="event-pic"><div><img src="art/${sp.file}" alt="" width="${(sp.size?.[0] ?? 160) * (sp.scale ?? 2)}" height="${(sp.size?.[1] ?? 100) * (sp.scale ?? 2)}"></div></div>` : '';
}

function eventDialog(run: RunState): string {
  const p = run.pending;
  if (p.kind !== 'event') return '';
  const def = getEncounter(p.eventId);
  if (def.letter && p.subject && !p.done && !p.optionId && recruitOption(run)) return applicationDialog(run);
  const wheel = p.done ? '' : wheelView(run);
  // E36: Was das Glücksrad bringt, steht erst da, wenn es stillsteht
  const late = (i: number) => (wheelJustSpun && i === p.log.length - 1 ? ' class="late"' : '');
  // T20 (Fabi): Jeder neue Text kommt wie ein neues Fenster. Nach einer Wahl steht nur noch, was sie gebracht hat;
  // alles davor steht in der Chronik
  const from = p.log.length ? Math.max(0, Math.min(ui.logMark, p.log.length - 1)) : 0;
  const stepKey = `${p.eventId}:${from}:${p.log.length}`;
  const fresh = stepKey !== ui.shownStep && p.log.length > 0;
  ui.shownStep = stepKey;
  const log = p.log.length ? `<ul class="log${fresh ? ' step-in' : ''}">${p.log.slice(from).map((l, i) => `<li${late(i + from)}>${markItems(l).replace(/\n/g, '<br>')}</li>`).join('')}</ul>` : '';
  const letter = p.letter ? `<blockquote class="letter">${p.letter.split('\n').map(esc).join('<br>')}</blockquote>` : '';
  let actions = '';
  // T36: Wie viele Möglichkeiten gibt es wirklich? Bei nur einer gibt es nichts aufzuklappen
  const choices = !p.done && !p.optionId ? eventOptions(run).filter((o) => !o.hidden) : [];
  // T36: Im Geschenk-Fenster sagt der Knopf, was die Tiere sagen würden („Danke!“)
  const thanks = p.auto && p.optionId ? eventOptions(run).find((o) => o.id === p.optionId)?.text : undefined;
  if (p.done) actions = `<div class="options"><button class="primary" data-act="leave">${esc(thanks ?? 'Zurück zum Boot')} <kbd class="pad">A</kbd></button></div>`;
  else if (p.optionId && p.attemptsLeft) {
    // E26, E33: Wie viele Versuche bleiben und wie gut die Chance steht; beim Fuchs gibt es kein Ende
    const left = p.attemptsLeft > 100 ? '' : `Noch ${p.attemptsLeft} ${p.attemptsLeft === 1 ? 'Versuch' : 'Versuche'}. `;
    // T19: Beim Ratespiel verrät das Spiel die Chance nicht
    const secret = attemptDef(run)?.secret;
    const chance = p.attemptChance !== undefined && !secret ? `Chance beim nächsten Versuch: ${pct(p.attemptChance)}.` : '';
    actions = `<p>${left}${chance}</p><div class="options">
      <button class="primary" data-act="retry">Nochmal versuchen</button><button data-act="giveup">${p.attemptsLeft > 100 ? 'Aufhören' : 'Aufgeben'}</button></div>`;
  } else if (choices.length > 1 && ui.optionsShown !== revealKey(p)) {
    // T32 (Fabi, Phase Kern, 17.10.2026): erst lesen, dann wählen; die Wahl klappt erst mit dem Knopf auf. T36: nur, wenn
    // es wirklich etwas zu wählen gibt, und der Knopf darf ein Tier aus dem Landtrupp nennen
    actions = `<div class="options"><button class="primary" data-reveal="${esc(revealKey(p))}">${esc(revealLabel(run, def.reveal, revealKey(p), p.party))} <kbd class="pad">A</kbd></button></div>`;
  } else {
    // Spieltest Phase 4: Optionen für Spezies, die nicht dabei sind, gar nicht zeigen
    actions = `<div class="options reveal">${eventOptions(run)
      .filter((o) => !o.hidden)
      .map((o) =>
        // E24 (Fabi): Wer klettert, sucht man sich aus. Fabi (Phase Kern): Wer es gut kann, verrät das Spiel nicht, man verlässt sich auf sein Wissen über Waldtiere
        o.actors && o.available
          ? `<div class="opt-group"><p>${esc(o.text)}: ${esc(o.ask ?? 'Wer soll es machen?')}</p>${o.actors.map((a) => `<button data-actor="${esc(o.id)}|${a.id}">${esc(a.name)}</button>`).join('')}</div>`
          : o.gifts && o.available
            ? giftChoice(o.id, o.text, o.hint, o.gifts)
            : `<button data-opt="${esc(o.id)}" ${o.available ? '' : 'disabled'}>${esc(o.text)}${o.hint ? `<small>${esc(o.hint)}</small>` : ''}${o.reason ? `<small>(${esc(o.reason)})</small>` : ''}</button>`,
      )
      .join('')}</div>`;
  }
  // Runde 8 (Fabi): Kann ein Tier an Bord kommen, sieht man vorher, wen man schon hat (mit den Optionen)
  const crew = !p.done && !p.optionId && recruitOption(run) && ui.optionsShown === revealKey(p) ? crewOverview(run, p.subject ?? undefined) : '';
  // T36: Gab es nichts zu wählen (ein Dank, ein Geschenk), stehen Einleitung und Ergebnis zusammen
  const intro = p.log.length && !p.auto ? '' : `${paras(eventText(run))}`;
  return `<h2>${esc(def.name)}</h2>${eventPicture(p.eventId)}${intro}${letter}${wheel}${log}${crew}${actions}`;
}

/** T32: Jeder neue Schritt einer Begegnung will erst gelesen werden */
const revealKey = (p: { eventId: string; log: string[] }) => `${p.eventId}:${p.log.length}`;

/** Ein fester, aber gestreuter Index aus einem Text, damit sich der Knopf beim Neuzeichnen nicht ändert */
function spread(key: string, n: number): number {
  let h = 7;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return n > 0 ? h % n : 0;
}

/**
 * T36 (Fabi, Phase Kern, 17.10.2026 nachts): Der Knopf zum Aufklappen passt zur Begegnung und nennt gern ein Tier aus dem
 * Landtrupp („{jemand} hat schon eine Idee“), damit die Tiere Charakter bekommen
 */
function revealLabel(run: RunState, own: Wording | undefined, key: string, party: string[]): string {
  const list = own === undefined ? runRules.revealTexts ?? ['Was macht ihr?'] : Array.isArray(own) ? own : [own];
  const names = party.map((id) => run.crew.find((f) => f.id === id)?.name).filter((n): n is string => !!n);
  const text = list[spread(key, list.length)] ?? 'Was macht ihr?';
  if (text.includes('{jemand}') && !names.length) return 'Was macht ihr?';
  return text.replace('{jemand}', names[spread(`${key}:wer`, names.length)] ?? '');
}

/** Runde 8 (Fabi): Erst die Option, dann das Geschenk, gerahmt wie im Gepäck und mit Hover-Info */
function giftChoice(id: string, text: string, hint: string | undefined, gifts: { id: string; ref: string; hint: string }[]): string {
  if (ui.giftFor !== id) return `<button data-giftopen="${esc(id)}">${esc(text)}${hint ? `<small>${esc(hint)}</small>` : ''}</button>`;
  const tiles = gifts
    .map((g) => `<button class="gift" data-opt="${esc(g.id)}"${tip([itemText(g.ref)])}>${itemName(g.ref)} ${itemTag(g.ref)} ${rarityTag(getItem(g.ref).rarity)}<small>${esc(g.hint)}</small></button>`)
    .join('');
  return `<div class="opt-group"><p>${esc(text)}: Was wollt ihr hergeben?${hint ? `<small>${esc(hint)}</small>` : ''}</p><div class="gift-grid">${tiles}</div><button data-giftback="1">Doch nicht</button></div>`;
}

const WHEEL_COLORS = ['#e9b949', '#c9ced6', '#d98c5f', '#c47b54', '#6fae6a', '#8cc084', '#a3cf9b', '#7aa6d8', '#9dbbe0', '#b5cbe8', '#d9d2c5'];
let wheelSpins = 0;
let wheelAngle = 0;
let wheelJustSpun = false;

/** E36 Elster-Glücksrad (Fabi): Felder so breit, wie sie wahrscheinlich sind; das Rad dreht sich und bleibt oben stehen */
function wheelView(run: RunState): string {
  const p = run.pending;
  if (p.kind !== 'event') return '';
  const o = eventOptions(run).find((x) => x.wheel);
  wheelJustSpun = false;
  if (!o?.wheel) return '';
  const total = o.wheel.reduce((a, s) => a + s.weight, 0);
  let from = 0;
  const stops: string[] = [];
  const mids: number[] = [];
  o.wheel.forEach((s, i) => {
    const to = from + (s.weight / total) * 360;
    stops.push(`${WHEEL_COLORS[i % WHEEL_COLORS.length]} ${from}deg ${to}deg`);
    mids.push((from + to) / 2);
    from = to;
  });
  const hit = p.wheel?.segment;
  // Nur bei einem neuen Dreh läuft die Drehung ab, sonst steht das Rad still
  const spins = p.wheel?.spins ?? 0;
  const start = wheelAngle;
  if (hit !== undefined && spins !== wheelSpins) {
    wheelSpins = spins;
    const target = -mids[hit]!;
    wheelAngle = start + ((((target - start) % 360) + 360) % 360) + 3 * 360;
  } else if (!spins) wheelSpins = wheelAngle = 0;
  const animate = hit !== undefined && start !== wheelAngle;
  wheelJustSpun = animate;
  const legend = o.wheel
    .map((s, i) => `<li class="${i === hit ? 'hit' : ''}"><span class="swatch" style="background:${WHEEL_COLORS[i % WHEEL_COLORS.length]}"></span>${esc(s.label)}</li>`)
    .join('');
  return `<div class="wheel-box"><div class="wheel-wrap"><div class="wheel-pointer">▼</div>
    <div class="wheel ${animate ? 'spinning' : ''}" style="background:conic-gradient(${stops.join(',')});--from:${start}deg;--to:${wheelAngle}deg;transform:rotate(${wheelAngle}deg)"></div></div>
    <ul class="wheel-legend">${legend}</ul></div>`;
}

function traderDialog(run: RunState): string {
  const p = run.pending;
  if (p.kind !== 'trader') return '';
  const offers = p.offers
    .map((o, i) => `<button data-buy="${i}" ${run.wood < o.price ? 'disabled' : ''}>${itemName(o.item)} · ${o.price} Holz ${itemTag(o.item)}<small>${esc(itemText(o.item))}</small></button>`)
    .join('');
  return `<h2>Händler</h2>
    <p>Ein Händler hat sein Zelt am Ufer aufgeschlagen. Ihr habt <b>${run.wood} Holz</b>.</p>
    ${slotLine(run)}
    ${p.log.length ? `<ul class="log">${p.log.map((l) => `<li>${slyMark(run, p.party, l)}${markItems(l)}</li>`).join('')}</ul>` : ''}
    <div class="options">${offers || '<p class="hint">Ausverkauft.</p>'}<button class="primary" data-act="leave">Zurück zum Boot</button></div>`;
}

/** S13 Fuchs, Schlau (Runde 7): Wo der Fuchs herunterhandelt, steht das Zeichen der Passive, wie es gerade greift (Bild 1) */
function slyMark(run: RunState, party: string[], line: string): string {
  const fox = party.map((id) => run.crew.find((f) => f.id === id)).find((f) => f && getSpecies(f.species).passive === 'schlau');
  return fox && line.startsWith(`${fox.name} handelt`) ? slyIcon() : '';
}
function slyIcon(): string {
  const sp = sprite('ui', 'passiv-schlau');
  if (!sp) return '';
  const frame = sp.states?.greift ?? 1;
  return `<i class="passive-art" style="background-image:url(art/${sp.file});background-position:-${frame * 20}px 0" aria-hidden="true"></i>`;
}

/** P10 (Fabi, Runde 8): Vor dem Kaufen sehen, wer noch Platz für ein dauerhaftes Item hat */
function slotLine(run: RunState): string {
  const slots = itemSlots(run);
  const free = run.crew.filter((f) => f.items.length < slots);
  const waiting = run.inventory.filter((i) => getItem(i).kind === 'equip').length;
  return `<p class="hint">Platz für dauerhafte Items: ${run.crew.map((f) => `${esc(f.name)} ${f.items.length} von ${slots}`).join(' · ')}${free.length ? '' : ' (alle voll)'}${waiting ? `. Im Inventar warten schon ${waiting} dauerhafte.` : '.'}</p>`;
}

/**
 * T29 (Fabi, Phase Kern): Wie ein Auftrag meistens ausgeht, in einem Satz statt in Prozenten. T36 (Fabi, 17.10.2026
 * nachts): Was er bringen kann, steht konkret da („ein Level, ein Item oder ein neues Tier“), die Gefahr in einem Satz
 */
function errandOdds(def: EventDef): { reward: string; risk: string } {
  const outs = def.outcomes ?? [];
  const share = (pred: (e: EventEffect) => boolean) => outs.filter((o) => (o.effects ?? []).some(pred)).reduce((a, o) => a + o.chance, 0);
  const hurt = share((e) => !!(e.injure || e.injureCurrent));
  const gone = share((e) => !!e.farewell);
  // Was es bringen kann, das Häufigste zuerst
  const gains: [number, string][] = [];
  const add = (pred: (e: EventEffect) => boolean, word: (e: EventEffect) => string) => {
    const hit = outs.filter((o) => (o.effects ?? []).some(pred));
    if (!hit.length) return;
    const e = hit[0]!.effects!.find(pred)!;
    gains.push([hit.reduce((a, o) => a + o.chance, 0), word(e)]);
  };
  add((e) => !!e.levelUp, (e) => (e.levelUp === 1 ? 'ein Level für den, der geht' : `${e.levelUp} Level`));
  add((e) => !!e.item, (e) => (e.item && e.item !== 'random' && e.item !== 'equip' && e.item !== 'consumable' ? `${getItem(e.item).gender === 'f' ? 'eine' : 'ein'} ${itemLabel(e.item)}` : 'ein Item'));
  add((e) => !!e.recruit, () => 'ein neues Tier, das mitkommt');
  add((e) => !!e.scoutBoss, () => 'ihr erfahrt, wer am Durchlass wartet');
  add((e) => !!e.xpParty, (e) => `${Array.isArray(e.xpParty) ? e.xpParty[0] : e.xpParty} XP`);
  add((e) => !!e.wood && !Array.isArray(e.wood) ? (e.wood as number) > 0 : !!e.wood, () => 'Holz');
  gains.sort((a, b) => b[0] - a[0]);
  const words = gains.map(([, w]) => w);
  const reward = words.length ? `Lohn: ${words.length > 1 ? `${words.slice(0, -1).join(', ')} oder ${words[words.length - 1]}` : words[0]}.` : '';
  const risk = hurt > 0 ? `${hurt >= 0.3 ? 'Oft' : 'Manchmal'} kommt er verletzt zurück${gone > 0 ? ', ganz selten gar nicht' : ''}.` : gone > 0 ? 'Ganz selten kommt er gar nicht wieder.' : '';
  return { reward, risk };
}

function errandDialog(run: RunState): string {
  const p = run.pending;
  if (p.kind !== 'errand') return '';
  const def = getEncounter(p.eventId);
  if (p.done) {
    return `<h2>${esc(def.name)}</h2>${eventPicture(p.eventId)}<ul class="log">${p.log.map((l) => `<li>${markItems(l).replace(/\n/g, '<br>')}</li>`).join('')}</ul>
      <div class="options"><button class="primary" data-act="leave">Zurück zum Boot</button></div>`;
  }
  // T32, T36 (Fabi, Phase Kern): Erst die Geschichte lesen; wer geht und was es bringt, klappt erst danach auf
  const key = `${p.eventId}:auftrag`;
  if (ui.optionsShown !== key) {
    return `<h2>${esc(def.name)}</h2>${paras(eventText(run))}
      <div class="options"><button class="primary" data-reveal="${esc(key)}">${esc(revealLabel(run, def.reveal, key, p.party))} <kbd class="pad">A</kbd></button></div>`;
  }
  const { reward, risk } = errandOdds(def);
  // Runde 7: Wer als Letzter an Bord ist, kann nicht gehen
  const alone = run.crew.filter((f) => !f.away).length <= 1;
  const who = alone
    ? `<p class="hint">Allein an Bord kann niemand gehen.</p>`
    : p.party
    .map((id) => run.crew.find((f) => f.id === id))
    .filter((f): f is RunFriend => !!f)
    .map((f) => `<button data-send="${f.id}">${esc(displayName(f))} schicken<small>${esc(getClass(f.friendClass).name)} · Level ${f.level} · ${pct(f.hp / maxHp(f, run))} Leben</small></button>`)
    .join('');
  // Runde 8 (Fabi): Wer fehlt, fehlt auch am Ufer
  const here = run.crew.filter((f) => !f.away).length;
  return `<h2>${esc(def.name)}</h2>${paras(eventText(run))}
    <p class="reward">${esc(reward)}</p>
    <p class="hint">${esc(risk)} Wer geht, fehlt zwei Anlegestellen lang${here - 1 < fieldSlotsOf(run) ? `; so lange seid ihr am Ufer nur zu ${here - 1}` : ''}.</p>
    <div class="options reveal">${who}<button data-send="">Ablehnen</button></div>`;
}

/** Spieltest Phase 4 (Fabi): Klasse und Level stehen groß oben rechts, sie sind neben dem Namen das Wichtigste */
function classBadge(f: RunFriend): string {
  const cls = getClass(f.friendClass);
  return `<div class="fd-class"><span class="fd-cls"><span class="dot" style="background:${CLASS_COLORS[f.friendClass]}"></span>${esc(cls.name)}</span>${cls.text ? `<small>${esc(cls.text)}</small>` : ''}<span class="fd-level">Level ${f.level}</span></div>`;
}

/** Spieltest Phase 4 (Fabi): Hover-Info; die Zeilen stehen untereinander */
const tip = (lines: (string | false | null | undefined)[]) => ` data-tip="${esc(lines.filter(Boolean).join('\n'))}"`;

/** Woher ein Wert kommt: Klasse auf Level 1, mit dem Level, mit der eigenen Anlage (F37), mit Ästen, Items und Stärkungen */
function valueOrigin(base: number, grown: number, now: number, level: number, anlage = 1, unit = ''): string[] {
  const out = [`Grundwert der Klasse auf Level 1: ${Math.round(base)}${unit}`];
  if (Math.abs(grown - base) > 0.5) out.push(`mit Level ${level}: ${Math.round(grown)}${unit}`);
  if (Math.abs(anlage - 1) > 1e-6) out.push(`mit der eigenen Anlage (${anlage > 1 ? '+' : '−'}${pct(Math.abs(anlage - 1))}): ${Math.round(grown * anlage)}${unit}`);
  if (Math.abs(now - grown * anlage) > 0.5) out.push(`mit Ästen, Items und Stärkungen: ${Math.round(now)}${unit}`);
  return out;
}

type LuckKey = keyof typeof STAR_WORDS;
const isStar = (f: RunFriend, key: LuckKey) => (f.rolls?.[key] ?? 0) >= runRules.luck.starAt - 1e-9;
const starMark = (f: RunFriend, key: LuckKey) => (isStar(f, key) ? ' <span class="star">★</span>' : '');
const starLine = (f: RunFriend, key: LuckKey) => isStar(f, key) && `★ ${f.name} ist besonders ${STAR_WORDS[key]}.`;
/** Faktor der eigenen Anlage auf einen Wert */
const anlage = (f: RunFriend, key: LuckKey) => 1 + (f.rolls?.[key] ?? 0) * runRules.luck.friend[key];

/** Q57: Auf der Vorderseite nur eine knappe Zeile; der ganze Text steht in der Hover-Info */
const firstSentence = (t: string, max = 80) => {
  const s = /^(.*?[.!?])(\s|$)/.exec(t)?.[1] ?? t;
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  return `${cut.slice(0, cut.lastIndexOf(' ')).replace(/[,;:]$/, '')} …`;
};
const ORIGIN: Record<RunFriend['origin']['how'], string> = { start: 'von Anfang an dabei', befreit: 'befreit', ereignis: 'unterwegs gefunden', auftrag: 'von einem Auftrag mitgebracht' };

/**
 * Q57 (Fabi, 15.10.2026): Die Freundeskarte ist eine Sammelkarte. Vorne alles, was man zum Entscheiden braucht, auf
 * einen Blick und für jeden Freund an derselben Stelle; was dahintersteckt, zeigt das Darüberfahren. F46 (Fabi,
 * 29.10.2026; Runde 7): als Rindenkarte nach Fassung A des Grafik-Leads (karte.ts), mit dem Brustbild vor der Landschaft,
 * in der ihr euch kennengelernt habt; die Eignung an Bord steht unter der Karte
 */
function cardData(run: RunState, f: RunFriend): CardData {
  const sp = getSpecies(f.species);
  const entry = sp.classes.find((c) => c.friendClass === f.friendClass)!;
  const cls = getClass(f.friendClass);
  const prof = combatProfile(f, run);
  const st = prof.stats;
  const kniff = getAbility(prof.kniff ?? defaultKniff(entry)!);
  const passive = getAbility(sp.passive);
  const heal = prof.attackHeal ?? 0;
  const share = Math.max(0, Math.min(1, f.hp / st.hp));
  const grow = (g: number) => 1 + g * (f.level - 1);
  const mutMax = Math.round(combatRules.mut.max * (prof.perks?.mutNeed ?? 1));
  // S10: Die Leibwache ist zu Kampfbeginn bereit
  const startMut = kniff.params.readyAtStart ? mutMax : Math.round(Math.min(combatRules.mut.startMax, prof.perks?.startMut ?? 0) * mutMax);
  const mutGain = prof.perks?.mutGain ?? 1;
  const tree = maxTreeHp(f, run);
  const name = f.name;
  // S13 Fuchs, Schlau: die Volltreffer aus der Passive zählen mit
  const crit = Math.min(1, st.critChance + (passive.params.critBonus ?? 0));
  const damageOrigin = valueOrigin(cls.stats.damage, cls.stats.damage * grow(runRules.levelGrowth.damage), st.damage, f.level, anlage(f, 'damage'));
  const trait = traitOfEntry(entry);
  const classTip = [`${cls.name}: ${cls.text}`, trait && `${trait.name}: ${describe(trait.text, f, run)}`];
  const passiveText = describe(passive.text, f, run);
  const kniffText = describe(prof.kniffText ?? kniff.text, f, run);
  const steps = [f.branch, ...f.nodes].filter(Boolean).length;
  const way = pathSteps(f).map((s) => `Level ${s.level}${s.branch ? ', Ast' : ''}: ${s.name}. ${s.text}`);
  const t = f.tally;
  const home = biomeOfDay(f.origin.day);
  const boosts = activeBoosts(f);
  const apt = `<div class="aptline"${tip(['An Bord: wie gut auf jedem Posten.'])}>${(['kaeptn', 'ausguck', 'gemeinschaft', 'heiler'] as PostId[]).map((p) => `<span class="apt-${aptitude(f.species, p)}" title="${esc(`${POST_INFO[p].name}: ${APT_WORD[aptitude(f.species, p)]}`)}">${postIcon(p, true)} ${APT_WORD[aptitude(f.species, p)]}</span>`).join('')}</div>`;
  const hc = hpClass(share);
  return {
    name,
    species: f.species,
    speciesName: sp.name,
    article: sp.article,
    look: f.look ?? 0,
    home: home.id,
    friendClass: f.friendClass,
    className: cls.name,
    classTip: classTip.filter((x): x is string => !!x),
    level: f.level,
    levelTip: [f.level >= runRules.xp.maxLevel ? 'Höchstes Level.' : `Noch ${Math.max(0, xpToNext(f.level) - Math.max(0, f.xp - xpForLevel(f.level)))} XP bis Level ${f.level + 1}.`],
    wounded: f.wound ? `${f.name} wurde${f.wound.by ? ` ${fromWhom(f.wound.by)}` : ''} verwundet. Fällt ${f.name} heute noch einmal, nimmt ${f.name} Abschied. Die Wunde heilt über Nacht.` : null,
    away: f.away ? 'unterwegs' : null,
    // S5 (Fabi, Abschlusstest Phase 4): Wer mit jedem Angriff heilt, zeigt groß die Heilung; der Schaden steht im Darüberfahren
    damage: heal > 0
      ? { heal: true, value: Math.round(heal), star: isStar(f, 'damage'), tip: [`Ist ein Freund verletzt, heilt jeder Angriff den, dem am meisten fehlt, um ${Math.round(heal)} Leben.`, `Sind alle gesund, greift ${name} an und macht ${Math.round(st.damage)} Schaden.`, ...valueOrigin(entry.attackHeal ?? 0, (entry.attackHeal ?? 0) * grow(runRules.levelGrowth.damage), heal, f.level, anlage(f, 'damage')), starLine(f, 'damage')].filter((x): x is string => !!x) }
      : { heal: false, value: Math.round(st.damage), star: isStar(f, 'damage'), tip: [`Jeder Angriff macht ${Math.round(st.damage)} Schaden, ein Volltreffer mehr.`, ...damageOrigin, starLine(f, 'damage')].filter((x): x is string => !!x) },
    speed: {
      value: fmt(1 / st.attackInterval, 2),
      star: isStar(f, 'attackSpeed'),
      tip: [
        `Alle ${fmt(st.attackInterval, 2)} s ein Angriff.`,
        Math.abs(st.attackInterval - cls.stats.attackInterval) > 1e-6 && `Grundwert der Klasse: alle ${fmt(cls.stats.attackInterval, 2)} s.`,
        // P10 (Fabi, Abschlusstest Phase 4): die Reichweite im selben Wortlaut wie auf der Info-Tafel im Kampf
        st.range > 10 ? `Greift aus der Entfernung an, Reichweite ${Math.round(st.range)}.` : `Muss nah an den Gegner heran, Reichweite ${Math.round(st.range)}.`,
        starLine(f, 'attackSpeed'),
      ].filter((x): x is string => !!x),
    },
    mut: {
      start: startMut,
      max: mutMax,
      star: isStar(f, 'startMut'),
      tip: [`Ist der Mut voll (${mutMax}), kommt der Kniff.`, startMut > 0 ? `${name} geht schon mit ${startMut} Mut in den Kampf.` : `${name} beginnt jeden Kampf ohne Mut.`, `Jeder eigene Angriff bringt ${Math.round(combatRules.mut.perAttack * mutGain)} Mut, und wer einsteckt, wird auch mutiger.`, Math.abs(mutGain - 1) > 1e-6 && `Der Mut füllt sich ${pct(mutGain)} so schnell wie sonst.`, starLine(f, 'startMut')].filter((x): x is string => !!x),
    },
    life: {
      hp: f.hp,
      max: st.hp,
      share,
      level: hc.includes('low') ? 'knapp' : hc.includes('mid') ? 'mittel' : 'gut',
      star: isStar(f, 'hp'),
      tip: [...valueOrigin(cls.stats.hp, cls.stats.hp * grow(runRules.levelGrowth.hp), st.hp, f.level, anlage(f, 'hp')), 'Auf der Weiterfahrt und an Bord erholt man sich.', starLine(f, 'hp'), tree !== null && `Baum ${Math.round(f.treeHp ?? tree)} / ${Math.round(tree)}: ${prof.perks?.treeThrow ? 'Solange sein Baum steht, sitzt es oben, wirft Nüsse auf Gegner und nutzt seinen Kniff.' : 'Solange sein Baum steht, sitzt es oben und springt nur für seinen Kniff hinunter.'}`].filter((x): x is string => !!x),
    },
    passive: { id: passive.id, name: passive.name, text: passiveText, first: firstSentence(passiveText, 150) },
    kniff: { name: kniff.name, text: kniffText, first: firstSentence(kniffText, 150) },
    items: { slots: itemSlots(run), list: f.items.map((it) => ({ short: itemLabel(it).slice(0, 2), tip: `${itemLabel(it)}: ${itemText(it)}` })) },
    way: { steps, open: f.pendingLevels > 0, tip: ['Der Weg: jeder Punkt eine Wahl beim Level-Up.', ...way] },
    under: `<div class="rk-under">${boosts.length ? `<div class="boostline">${boosts.map((b) => `<span class="ichip use"${tip([b.tip])}>${esc(b.label)}</span>`).join('')}</div>` : ''}${apt}</div>`,
    back: {
      sub: `${sp.article} ${sp.name} · ${cls.name} · Level ${f.level}`,
      origin: `${ORIGIN[f.origin.how]}, seit Tag ${f.origin.day}${f.titles.length ? ` · ${f.titles[f.titles.length - 1]}` : ''}`,
      quote: introText(f) ?? null,
      values: [
        { icon: st.range > 10 ? 'karte-fern' : 'karte-nah', text: st.range > 10 ? 'Fernkampf' : 'Nahkampf', tip: [`Reichweite ${Math.round(st.range)}`] },
        { icon: 'karte-volltreffer', text: pct(crit), tip: [`${pct(crit)} Chance, dass ein Angriff ein Volltreffer ist.`, `Ein Volltreffer macht ${pct(combatRules.crit.damageBonus)} mehr.`, crit > st.critChance && `Davon ${pct(crit - st.critChance)} aus der Passive ${passive.name}.`, starLine(f, 'critChance')].filter((x): x is string => !!x) },
        { icon: 'karte-lauftempo', text: `Tempo ${Math.round(st.moveSpeed)}`, tip: [`Wie schnell ${name} über das Ufer läuft: ${speedWord(st.moveSpeed)}.`, `Grundwert der Klasse: ${Math.round(cls.stats.moveSpeed)}, ${sp.name}: ${pct(sp.speed ?? 1)} davon.`, starLine(f, 'moveSpeed')].filter((x): x is string => !!x) },
        { icon: 'karte-kaempfe', text: `${t.fights} ${t.fights === 1 ? 'Kampf' : 'Kämpfe'}`, tip: [`${t.wins} gewonnen`, `Schaden ${Math.round(t.damage)}`, `eingesteckt ${Math.round(t.taken)}`, `geheilt ${Math.round(t.healed)}`] },
        { icon: 'karte-ufer', text: `${t.landings} Ufer`, tip: ['An so vielen Ufern war er dabei.'] },
        { icon: 'karte-besiegt', text: `${t.kills} besiegt`, tip: ['So viele Sturmwesen hat er vertrieben.'] },
      ],
      heading: `Was ${f.name} erlebt hat`,
      memories: topMemories(f, 4),
    },
  };
}

/** Q57: Vorder- und Rückseite übereinander; „Umdrehen“ wechselt, ohne das Fenster neu aufzubauen */
function friendCard(run: RunState, f: RunFriend): string {
  return rindenkarte(cardData(run, f));
}

/** Spieltest Phase 4 (Fabi): Bei einer Aufnahme will man sehen, wen man schon hat */
function crewOverview(run: RunState, candidate?: RunFriend): string {
  const rows = run.crew
    .map((f) => `<li><span class="dot" style="background:${CLASS_COLORS[f.friendClass]}"></span><b>${esc(f.name)}</b> <span class="hint">${esc(getSpecies(f.species).name)} · ${esc(getClass(f.friendClass).name)} · Level ${f.level} · ${pct(f.hp / maxHp(f, run))} Leben${f.items.length ? ` · ${f.items.map((i) => itemName(i)).join(', ')}` : ''}</span><button class="look" data-peek="${f.id}">Ansehen</button></li>`)
    .join('');
  return `<h3>Schon an Bord (${run.crew.length} von ${run.crewSlots} Plätzen)</h3>${classTally(run, candidate)}<ul class="crew-overview">${rows}</ul>`;
}

/**
 * P10 (Fabi, Runde 8): Jede Entscheidung braucht alle Infos. Bei einer Aufnahme: welche Klassen schon an Bord sind,
 * welche fehlen und was der Neue mitbringt
 */
function classTally(run: RunState, candidate?: RunFriend): string {
  const ids = ['dickfell', 'raufbold', 'springinsfeld', 'adlerauge', 'giftzahn', 'beschuetzer'];
  const count = (c: string) => run.crew.filter((f) => f.friendClass === c).length;
  const chip = (c: string) => `<span class="ctally${count(c) ? '' : ' none'}${candidate?.friendClass === c ? ' cand' : ''}"><span class="dot" style="background:${CLASS_COLORS[c]}"></span>${esc(getClass(c).name)} <b>${count(c)}</b></span>`;
  let line = '';
  if (candidate) {
    const n = count(candidate.friendClass);
    const cls = getClass(candidate.friendClass).name;
    line = n ? `<p class="hint">Die Klasse ${esc(cls)} habt ihr schon ${n === 1 ? 'einmal' : `${n}-mal`} an Bord.</p>` : `<p class="ok">Die Klasse ${esc(cls)} habt ihr noch nicht an Bord.</p>`;
  }
  return `<div class="class-tally">${ids.map(chip).join('')}</div>${line}`;
}

/** Wer Platz machen soll: mit allem, was man für die Wahl braucht */
function releaseButtons(run: RunState, attr: string): string {
  return run.crew
    .map((f) => `<button ${attr}="${f.id}">${esc(displayName(f))} bleibt zurück<small>${esc(getClass(f.friendClass).name)} · Level ${f.level} · ${pct(f.hp / maxHp(f, run))} Leben${f.items.length ? ` · lässt ${f.items.map((i) => itemName(i)).join(' und ')} für euch da` : ''}</small></button>`)
    .join('');
}

function recruitDialog(run: RunState): string {
  const p = run.pending;
  if (p.kind !== 'recruit') return '';
  const c = p.candidate;
  const full = run.crew.length >= run.crewSlots;
  const log = p.log.length ? `<ul class="log">${p.log.map((l) => `<li>${markItems(l)}</li>`).join('')}</ul>` : '';
  const release = full
    ? `<p class="warn">Alle ${run.crewSlots} Plätze sind belegt. Wer bleibt am Ufer zurück, damit ${esc(c.name)} Platz hat?</p>
       <div class="options">${releaseButtons(run, 'data-release')}</div>`
    : '';
  // F33: Jeder neue Freund kommt mit einem Namensvorschlag; Umbenennen stärkt die Bindung
  return `<div class="fd-head"><div><h2>${esc(displayName(c))} möchte mitreisen</h2></div>${classBadge(c)}</div>
    <div class="level-layout friend-layout">
      <div class="level-who">${friendCard(run, c)}</div>
      <div class="friend-side">
        ${log}
        ${introText(c) ? `<div class="speech">„${esc(introText(c)!)}“</div>` : ''}
        ${nameField(c)}
        ${crewOverview(run, c)}
        ${release}
        <div class="options">${full ? '' : '<button class="primary" data-act="accept">Willkommen an Bord!</button>'}<button data-act="decline">Ziehen lassen</button></div>
      </div>
    </div>`;
}

function reportDialog(run: RunState): string {
  const r = run.reports![0]!;
  const sp = getSpecies(r.species);
  // Fabi: Wer zurückkommt, erzählt mündlich; einen Zettel gibt es nur, wenn jemand fernbleibt
  const text = r.letter.split('\n').map(esc);
  return `<h2>${r.gone ? 'Ein Zettel am Ufer' : `${esc(r.name)} ist zurück!`}</h2>
    <p class="kicker">${esc(r.errand)} · ${esc(r.name)} ${esc(sp.article)} ${esc(sp.name)}</p>
    ${r.gone ? `<blockquote class="letter">${text.join('<br>')}</blockquote>` : `<p class="hint">${esc(r.name)} erzählt:</p><div class="speech">„${text.join(' ')}“</div>`}
    ${r.facts.length ? `<ul class="facts">${r.facts.map((f) => `<li>${markItems(f)}</li>`).join('')}</ul>` : ''}
    <div class="options"><button class="primary" data-act="report">${r.gone ? 'Gute Reise' : 'Willkommen zurück!'}</button></div>`;
}

/**
 * F38 (Lead-Auftrag, docs/bindung.md Nr. 1): Abschied als eigener Moment. Letzte Worte in der Stimme der Spezies,
 * was der Freund mit euch erlebt hat, eine Reaktion an Bord und ein einziger Knopf, mit dem ihr selbst etwas tut.
 */
function farewellDialog(run: RunState): string {
  const m = run.farewells![0]!;
  const f = run.gone.find((x) => x.id === m.friendId);
  const mem = f ? topMemories(f, 4) : [];
  return `<div class="farewell">
    <h2>Abschied von ${esc(f ? displayName(f) : m.name)}</h2>
    ${m.words ? `<div class="speech">„${esc(m.words)}“</div>` : ''}
    ${m.wound ? `<p class="hint">${esc(m.wound)}</p>` : ''}
    ${m.together ? `<p>${esc(m.together)}</p>` : ''}
    ${m.beginning ? `<p class="beginning">${esc(m.beginning)}</p>` : ''}
    ${mem.length ? `<h3>Was ${esc(m.name)} mit euch erlebt hat</h3><ul class="memories">${mem.map((x) => `<li><small>Tag ${x.day}</small>${esc(m.name)} ${esc(x.text)}.</li>`).join('')}</ul>` : ''}
    ${m.reaction ? `<p class="reaction">${esc(m.reaction)}</p>` : ''}
    <div class="options"><button class="primary" data-pennant="${esc(m.friendId)}">Einen Wimpel für ${esc(m.name)} aufhängen</button></div>
  </div>`;
}

function bossRewardDialog(run: RunState): string {
  const p = run.pending;
  if (p.kind !== 'bossReward') return '';
  return `<h2>Der Durchlass ist offen</h2>
    <p>Der Boss ist vertrieben. An Bord ist jetzt Platz für <b>${run.crewSlots}</b>. Zwischen den Trümmern liegt etwas. Ihr könnt eins davon mitnehmen:</p>
    <div class="choice-grid">${p.choices.map((id) => `<button data-artifact="${id}"><b>${esc(getArtifact(id).name)} ${rarityTag(getArtifact(id).rarity)}</b><span>${esc(getArtifact(id).text)}</span></button>`).join('')}</div>
    <p class="hint">${run.artifacts.length ? `Ihr habt schon: ${run.artifacts.map((a) => `<b>${esc(getArtifact(a).name)}</b> (${esc(getArtifact(a).text)})`).join(' · ')}` : 'Ihr habt noch kein Artefakt.'}</p>`;
}

function nightDialog(run: RunState): string {
  const names = run.crew.map((f) => esc(f.name));
  // F42 (Fabi, Phase Kern): Über Nacht heilen die Wunden
  const hurt = run.crew.filter((f) => f.wound).map((f) => esc(f.name));
  return `<h2>Nacht</h2>
    <p>Das Floß tuckert durch die Dunkelheit ins nächste Stück Fluss. ${names.join(', ')} schlafen eng beieinander. Alle erholen sich ${pct(runRules.regen.night)}.${hurt.length ? ` ${hurt.length === 1 ? `Die Wunde von ${hurt[0]} verheilt` : `Die Wunden von ${joinWords(hurt)} verheilen`} über Nacht.` : ''}</p>
    <p>Morgen ist am Ufer Platz für <b>${runRules.fieldSlots[run.day] ?? fieldSlotsOf(run)}</b> Freunde. Der Sturm ist wieder weiter weg, die Gegner aber stärker.</p>
    <div class="options"><button class="primary" data-act="morning">Ein neuer Morgen</button></div>`;
}

function namingDialog(run: RunState): string {
  return `<h2>Eure Freunde</h2>
    <p>Drei Freunde legen mit euch ab. Jeder hat schon einen Namen, aber vielleicht fällt euch ein besserer ein.</p>
    ${run.crew
      // F40 (Lead-Auftrag): Die Gründer stellen sich am Steg vor
      .map((f) => `<div class="intro-row"><p><label>${esc(getSpecies(f.species).article)} ${esc(getSpecies(f.species).name)} (${esc(getClass(f.friendClass).name)}) <input class="name-input" data-name="${f.id}" value="${esc(f.name)}" maxlength="24" /></label></p>${introText(f) ? `<div class="speech small">„${esc(introText(f)!)}“</div>` : ''}</div>`)
      .join('')}
    <div class="options"><button class="primary" data-act="named">Ablegen!</button></div>`;
}

function honorDialog(run: RunState): string {
  const h = run.honor!;
  const text = honorText(run)!;
  const f = run.crew.find((x) => x.id === h.friendId);
  if (!f) return '';
  // F34: ein Titel für eine besondere Tat; nicht zu feierlich. Namen immer aktuell (Spieltest Phase 4)
  return `<h2>${esc(text.headline)}</h2>
    <p><b>${esc(displayName(f))}</b> ${esc(text.deed)}. Wollt ihr das festhalten?</p>
    <p><label>Titel <input id="honor-title" class="name-input" value="${esc(text.suggestion)}" maxlength="32" /></label></p>
    <div class="options"><button class="primary" data-act="honor">So soll es heißen</button><button data-act="nohonor">Nein danke</button></div>`;
}

// ---------- Level-Up (F35, Spieltest Phase 4: Überblick, Zahlen vorher und nachher, bisheriger Weg) ----------

type Fmt = 'n' | 'pct' | 's' | 'dmg' | 'growth' | 'flag';
const PARAM_LABEL: Record<string, [string, Fmt]> = {
  damageFactor: ['Schaden', 'dmg'],
  bonusDamage: ['Zusatzschaden', 'pct'],
  marks: ['Schwachstellen pro Kniff', 'n'],
  duration: ['Dauer', 's'],
  damageReduction: ['weniger Schaden', 'pct'],
  reduction: ['weniger Schaden', 'pct'],
  selfReduction: ['weniger Schaden', 'pct'],
  radius: ['Reichweite', 'n'],
  attacks: ['Angriffe nur auf ihn', 'n'],
  reflect: ['Schaden zurück', 'pct'],
  growth: ['Zuwachs pro Sekunde', 'growth'],
  amount: ['Schild', 'n'],
  count: ['Schilde', 'n'],
  stacks: ['Giftstapel', 'n'],
  heal: ['Heilung', 'n'],
  hits: ['Treffer', 'n'],
  hitInterval: ['Zeit zwischen den Treffern', 's'],
  lingerSeconds: ['bleibt danach stehen', 's'],
  spread: ['springt weiter auf', 'n'],
  treeHp: ['Leben des Baums', 'n'],
  regenPerSecond: ['Erholung pro Sekunde', 'pct'],
  mutFillSeconds: ['Mut voll nach', 's'],
  flatReduction: ['weniger Schaden pro Treffer', 'n'],
  damagePerStackPerSecond: ['Gift pro Stapel und Sekunde', 'n'],
  stacksPerAttack: ['Giftstapel pro Angriff', 'n'],
  targets: ['Ziele', 'n'],
  splashShare: ['Splitter', 'pct'],
  splashRadius: ['Splitter-Reichweite', 'n'],
  damageBoost: ['mehr Schaden', 'pct'],
  maxHpShare: ['Extraschaden vom Leben des Ziels', 'pct'],
  burrowSeconds: ['unter der Erde', 's'],
  searchRadius: ['Suchweite', 'n'],
  allHeal: ['Heilung für alle', 'n'],
  shield: ['Schild', 'n'],
  'nussgeschenk.count': ['Freunde, die eine Nuss bekommen', 'n'],
  // Fabi (Phase Kern, 17.10.2026): Kein Wert darf als Code-Name auf der Karte landen, etwa „seekMark“ beim Eisvogel
  slow: ['langsamer', 'pct'],
  'glitzerklau.factor': ['Abgelenkte treffen mit', 'pct'],
  fromRecent: ['Schild aus dem Schaden der letzten Sekunden', 'pct'],
  minAmount: ['Schild mindestens', 'n'],
  recentSeconds: ['zählt die letzten', 's'],
  seekMark: ['zielt auf einen Gegner mit Schwachstelle', 'flag'],
  overSeconds: ['wirkt über', 's'],
  byRecent: ['geht an die, die zuletzt am meisten abbekommen haben', 'flag'],
};
/** Wie eine Eigenheit (perk) beim Level-Up heißt und wie ihr Wert aussieht; flag: gibt es oder nicht */
const PERK_LABEL: Record<string, [string, 'pct' | 'n' | 'flag']> = {
  revive: ['kommt einmal statt Abschied zurück ans Boot', 'flag'],
  xpPerKniff: ['XP pro Kniff', 'n'],
  poisonJump: ['Gift springt über, wenn ein Gegner fällt', 'flag'],
  poisonPersist: ['Giftstapel verfallen nicht', 'flag'],
  poisonHits: ['Giftstapel pro Treffer', 'n'],
  poisonBurn: ['brennendes Gift, so viel stärker', 'n'],
  poisonBurnSeconds: ['so viele Sekunden wirkt ein brennender Stapel', 'n'],
  splash: ['Splitter bei normalen Angriffen', 'pct'],
  splashRadius: ['Splitter-Reichweite', 'n'],
  selfDamage: ['Schaden an sich selbst', 'pct'],
  nutHeal: ['Heilung pro getroffener Nuss', 'n'],
  treeThrow: ['wirft vom Baum Nüsse', 'flag'],
  thorns: ['Stacheln: Anteil, der an den Angreifer zurückgeht', 'pct'],
  noCurl: ['rollt sich bei wenig Leben nicht mehr ein', 'flag'],
  lifesteal: ['sich stärken: Anteil des Schadens als Leben', 'pct'],
  rageLifesteal: ['im Heißhunger: Anteil des Schadens als Leben', 'pct'],
  lowHpHaste: ['schneller, je weniger Leben', 'pct'],
  critDamage: ['Volltreffer machen mehr Schaden', 'pct'],
  critEvery: ['jeder so-und-sovielte Angriff ein Volltreffer', 'n'],
  critMut: ['Mut pro Volltreffer', 'pct'],
  healStrike: ['Anteil der Heilung als Schaden', 'pct'],
  randomTarget: ['trifft einen zufälligen Gegner in Reichweite', 'flag'],
  weakestTarget: ['trifft den schwächsten Gegner in Reichweite', 'flag'],
  restBoost: ['erholt sich an Bord besser', 'pct'],
  shellGuard: ['mit wenig Leben weniger Schaden', 'pct'],
  sporeCloud: ['Sporenwolke bei wenig Leben, Sekunden', 'n'],
  amberGuard: ['Bernstein bei ganz wenig Leben, Sekunden', 'n'],
  edgeHide: ['je weniger Leben, desto weniger Schaden; bei vollem Leben mehr', 'pct'],
  edgeLifesteal: ['auf der Kante sich stärken: Anteil des Schadens als Leben', 'pct'],
  sting: ['Angriffe vergiften zusätzlich', 'pct'],
  venom: ['Anteil der Treffer als bleibendes Gift', 'pct'],
  kniffVenom: ['Kniffe hinterlassen bleibendes Gift', 'pct'],
};
const num = (x: number) => (Math.round(x * 10) / 10).toString().replace('.', ',');

function fmtValue(x: number, how: Fmt, damage: number): string {
  switch (how) {
    case 'pct':
      return fmtPct(x);
    case 's':
      return `${num(x)} s`;
    case 'dmg':
      return `${Math.round(x * damage)} (${Math.round(x * 100)} %)`;
    case 'growth':
      return `+${Math.round((x - 1) * 100)} %`;
    case 'flag':
      return x > 0 ? 'ja' : 'nein';
    default:
      return num(x);
  }
}

/** Was sich mit dieser Wahl ändert, als „vorher → nachher“ */
function choiceDiff(run: RunState, f: RunFriend, c: LevelNode | LevelBranch): string {
  const a = combatProfile(f, run);
  const b = profileWithChoice(f, run, c);
  const lines: string[] = [];
  const row = (label: string, from: string, to: string) => lines.push(`<li>${label}: ${from} → <b>${to}</b></li>`);
  const changed = (x: number, y: number) => Math.abs(x - y) > 1e-6;
  if (changed(a.stats.hp, b.stats.hp)) row('Leben', `${Math.round(a.stats.hp)}`, `${Math.round(b.stats.hp)}`);
  if (changed(a.stats.damage, b.stats.damage)) row('Schaden pro Angriff', `${Math.round(a.stats.damage)}`, `${Math.round(b.stats.damage)}`);
  if (changed(a.stats.attackInterval, b.stats.attackInterval)) row('Angriffe pro Sekunde', num(1 / a.stats.attackInterval), num(1 / b.stats.attackInterval));
  if (changed(a.stats.critChance, b.stats.critChance)) row('Volltreffer', pct(a.stats.critChance), pct(b.stats.critChance));
  if (changed(a.stats.range, b.stats.range)) row('Reichweite', num(a.stats.range), num(b.stats.range));
  if (changed(a.attackHeal ?? 0, b.attackHeal ?? 0)) row('Heilt pro Angriff', `${Math.round(a.attackHeal ?? 0)}`, `${Math.round(b.attackHeal ?? 0)}`);
  const mutA = a.perks?.mutGain ?? 1;
  const mutB = b.perks?.mutGain ?? 1;
  if (changed(mutA, mutB)) row('Mut füllt sich', `${Math.round(mutA * 100)} %`, `${Math.round(mutB * 100)} % so schnell`);
  if ((a.kniff ?? null) !== (b.kniff ?? null) && b.kniff) {
    const k = getAbility(b.kniff);
    lines.push(`<li>Neuer Kniff <b>${esc(k.name)}</b>: ${esc(fillNumbers(k.text, b.params, b.stats.damage))}</li>`);
  }
  const value = (p: Record<string, number> | undefined, key: string) => {
    const [ability, k] = key.split('.') as [string, string];
    return p?.[key] ?? getAbility(ability).params[k] ?? 0;
  };
  const keys = [...new Set([...Object.keys(a.params ?? {}), ...Object.keys(b.params ?? {})])];
  for (const key of keys) {
    const x = value(a.params, key);
    const y = value(b.params, key);
    if (!changed(x, y)) continue;
    const [ability, k] = key.split('.') as [string, string];
    const [label, how] = PARAM_LABEL[key] ?? PARAM_LABEL[k] ?? [k, 'n'];
    row(`${esc(getAbility(ability).name)}, ${label}`, fmtValue(x, how, a.stats.damage), fmtValue(y, how, b.stats.damage));
  }
  for (const key of new Set([...Object.keys(a.perks ?? {}), ...Object.keys(b.perks ?? {})])) {
    if (key === 'mutGain' || key === 'splashRadius') continue;
    const x = a.perks?.[key] ?? 0;
    const y = b.perks?.[key] ?? 0;
    if (!changed(x, y)) continue;
    const [label, how] = PERK_LABEL[key] ?? [key, 'n'];
    if (how === 'flag') lines.push(`<li>${x === 0 ? 'Neu' : 'Weg'}: ${esc(label)}</li>`);
    else row(esc(label), how === 'pct' ? pct(x) : num(x), how === 'pct' ? pct(y) : num(y));
  }
  return lines.length ? `<ul class="diff">${lines.join('')}</ul>` : '';
}

function choiceButton(run: RunState, f: RunFriend, c: LevelNode | LevelBranch): string {
  return `<button data-level="${esc(c.id)}"><b>${esc(c.name)}</b><span>${esc(describe(c.text, f, run, c.effects))}</span>${choiceDiff(run, f, c)}</button>`;
}

/**
 * F35: die bisherigen Schritte eines Freundes mit Namen und Wirkung. Abschlusstest Phase 4 (Fabi): Jeder Schritt zeigt
 * die Zahlen, die direkt nach ihm galten, nicht die heutigen, damit man sieht, was er gebracht hat.
 */
export function pathSteps(f: RunFriend): { level: number; name: string; text: string; branch: boolean }[] {
  const t = getLevelTree(f.species, f.friendClass);
  const chosen = [f.branch, ...f.nodes].filter((x): x is string => !!x);
  const branch = t?.branches.find((b) => b.id === f.branch);
  const optionsAt = (i: number): (LevelNode | LevelBranch)[] => (i === 0 ? (t?.branches ?? runRules.genericUpgrades) : (branch?.upgrades[i - 1] ?? runRules.genericUpgrades));
  return chosen.map((id, i) => {
    const pick = optionsAt(i).find((o) => o.id === id) ?? runRules.genericUpgrades.find((o) => o.id === id);
    return { level: i + 2, name: pick?.name ?? id, text: pick ? describeStep(pick.text, f, i) : '', branch: i === 0 };
  });
}

/**
 * Spieltest Phase 4 (Fabi): der Weg, den dieser Freund gegangen ist, Level für Level. Runde 6: nur das Gewählte, was
 * eine Wahl genau macht, steht in der Hover-Info.
 */
function skillPath(f: RunFriend, open: boolean): string {
  const steps = pathSteps(f).map((s) => `<li class="${s.branch ? 'branch' : ''}"${tip([s.text])}><small>Level ${s.level}${s.branch ? ', Ast' : ''}</small>${esc(s.name)}</li>`);
  if (open) steps.push(`<li class="open"><small>Level ${steps.length + 2}</small>jetzt wählen</li>`);
  if (!steps.length) return '<p class="hint">Noch keine Wahl getroffen. Mit Level 2 wählt man einen von zwei Ästen.</p>';
  return `<ol class="path">${steps.join('')}</ol>`;
}

/** Ausrüstung und was gerade wirkt, als kurze Abzeichen mit Hover-Info */
function gearChips(run: RunState, f: RunFriend): string {
  const chips = [
    ...f.items.map((i) => `<span class="ichip keep"${tip([itemText(i)])}>${esc(itemLabel(i))}</span>`),
    ...activeBoosts(f).map((b) => `<span class="ichip use"${tip([b.tip])}>${esc(b.label)}</span>`),
  ];
  const slots = itemSlots(run);
  return `<h3>Ausrüstung <small class="hint">${f.items.length} von ${slots} Plätzen</small></h3>
    ${chips.length ? `<div class="gear-chips">${chips.join('')}</div>` : '<p class="hint">Trägt nichts, und gerade wirkt auch nichts.</p>'}`;
}

/** Spieltest Phase 4 (Fabi), Runde 6: links die gewohnte Karte des Freundes, rechts abgesetzt die Wahl */
function levelDialog(run: RunState, f: RunFriend): string {
  const choices = levelChoices(f);
  const forLevel = f.level - f.pendingLevels + 1;
  const isBranch = !f.branch;
  return `<div class="level-layout">
      <div class="level-who">${friendCard(run, f)}</div>
      <div class="level-pick">
        <h2>Level ${forLevel}!</h2>
        <p class="kicker">${esc(f.name)} darf wählen${f.pendingLevels > 1 ? `, danach ${f.pendingLevels === 2 ? 'ist noch eine Wahl' : `sind noch ${f.pendingLevels - 1} Wahlen`} offen` : ''}.</p>
        <p>${isBranch ? 'Wählt den Ast, in den sich dieser Freund spezialisiert. Er bestimmt, welche Verbesserungen danach kommen.' : 'Wählt eine Verbesserung.'}</p>
        <div class="choice-grid one">${choices.map((c) => choiceButton(run, f, c)).join('')}</div>
        <div class="options"><button data-act="later">Später entscheiden</button></div>
      </div>
    </div>`;
}

/** S12 (Fabi, 29.10.2026): Worin die Spezies an Bord gut ist, auf einen Blick */
export function aptitudeRow(species: string): string {
  const cells = (['kaeptn', 'ausguck', 'gemeinschaft', 'heiler'] as PostId[]).map((p) => {
    const a = aptitude(species, p);
    return `<span class="chip apt-${a}" title="${esc(POST_INFO[p].name)}: ${APT_WORD[a]}">${postIcon(p, true)} ${esc(POST_INFO[p].name)} <i>${APT_WORD[a]}</i></span>`;
  });
  return `<h3>An Bord</h3><div class="apt-row">${cells.join('')}</div>`;
}

function friendDialog(run: RunState, id: string): string {
  const f = run.crew.find((x) => x.id === id);
  if (!f) return '';
  const slots = itemSlots(run);
  const equipable = run.inventory.filter((i) => getItem(i).kind === 'equip');
  const usable = run.inventory.filter((i) => getItem(i).kind === 'consumable');
  const peek = ui.dialog?.kind === 'friend' && ui.dialog.peek;
  const give = peek
    ? ''
    : [
        ...(f.items.length < slots ? equipable.map((i) => `<button data-equip="${esc(i)}">${itemName(i)} anlegen<small>${esc(itemText(i))}</small></button>`) : []),
        ...usable.map((i) => `<button data-use="${esc(i)}">${itemName(i)} benutzen<small>${esc(itemText(i))}</small></button>`),
      ].join('');
  const levelOpen = f.pendingLevels > 0 && levelChoices(f).length > 0;
  // Q57: links die Karte, rechts, was man mit dem Freund tun kann
  return `<div class="level-layout friend-layout">
      <div class="level-who">${friendCard(run, f)}</div>
      <div class="friend-side">
        ${peek ? `<h2>${esc(displayName(f))}</h2>` : `<label class="rename"><span class="hint">Name</span> <input id="friend-name" class="name-input soft" value="${esc(f.name)}" maxlength="24" title="Zum Umbenennen hineinklicken" /> <span class="hint">✎ umbenennen</span></label>`}
        ${!peek && levelOpen ? '<div class="options"><button class="primary" data-act="levelnow">Level-Up wählen</button></div>' : ''}
        <h3>Weg</h3>${skillPath(f, levelOpen)}
        ${aptitudeRow(f.species)}
        ${gearChips(run, f)}
        ${give ? `<h4>Aus dem Gepäck geben</h4><div class="options">${give}</div>` : ''}
        <div class="options"><button class="primary" data-act="close">${peek ? 'Zurück' : 'Fertig'} <kbd class="pad">B</kbd></button></div>
      </div>
    </div>`;
}

function itemDialog(run: RunState, item: string): string {
  const def = getItem(item);
  const slots = itemSlots(run);
  const who = run.crew
    .map((f) => {
      const ok = def.kind === 'consumable' || f.items.length < slots;
      const has = f.items.length ? ` · trägt ${f.items.map((i) => itemName(i)).join(', ')}` : '';
      return `<button data-give="${f.id}" ${ok ? '' : 'disabled'}>${esc(displayName(f))}<small>${esc(getClass(f.friendClass).name)} · Level ${f.level} · ${pct(f.hp / maxHp(f, run))} Leben${def.kind === 'equip' ? ` · ${f.items.length} von ${slots} Plätzen${has}` : ''}</small></button>`;
    })
    .join('');
  return `<h2>${esc(def.name)} ${itemTag(item)}</h2><p>${esc(itemText(item))}</p>
    <div class="options">${who}<button data-act="close">Abbrechen</button></div>`;
}

function menuDialog(): string {
  return `<h2>Menü</h2><div class="options">
    <button class="primary" data-act="close">Weiterspielen</button>
    <button data-act="title">Zum Titel (der Run bleibt gespeichert)</button>
  </div>`;
}

/** Klicks im Dialog in Run-Aktionen übersetzen */
export function bindDialog(app: RunApp): void {
  const run = app.run;
  const card = $('dialog-card');
  const val = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value ?? '';
  card.onclick = (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>('button');
    if (!btn || btn.disabled) return;
    const d = btn.dataset;
    if (d.peek) {
      // Spieltest Phase 4 (Fabi): kurz einen Freund ansehen, die Aufnahme wartet
      ui.dialog = { kind: 'friend', id: d.peek, peek: true };
      app.sync();
      return;
    }
    if (d.welcome !== undefined) {
      // E28: Willkommen an Bord, in einem Schritt; ist das Boot voll, mit der Wahl, wer zurückbleibt
      const opt = recruitOption(run);
      const p = run.pending;
      if (!opt || p.kind !== 'event' || !p.subject) return;
      p.subject.name = val('recruit-name').trim().slice(0, 24) || p.subject.name;
      chooseOption(run, opt.id);
      if (run.pending.kind === 'recruit') acceptRecruit(run, d.welcome || undefined);
      app.sync();
      return;
    }
    if (d.reveal !== undefined) {
      // T32 (Fabi, Phase Kern): Der Text ist gelesen, jetzt kommen die Möglichkeiten
      ui.optionsShown = d.reveal;
      app.sync();
      return;
    }
    if (d.letterRead !== undefined) {
      // T20: Der Brief ist gelesen, jetzt kommt alles zum Entscheiden
      if (run.pending.kind === 'event') ui.letterRead = letterKey(run.pending);
      app.sync();
      return;
    }
    if (d.flip) {
      // Q57: Karte umdrehen, ohne das Fenster neu aufzubauen
      btn.closest('.rk')?.classList.toggle('flipped');
      return;
    }
    if (d.pennant) {
      hangPennant(run, d.pennant);
      app.sync();
      return;
    }
    if (d.giftopen || d.giftback) {
      ui.giftFor = d.giftopen ?? null;
      app.sync();
      return;
    }
    ui.giftFor = null;
    // T20: Was nach dieser Wahl in den Verlauf kommt, ist der neue Schritt
    if (run.pending.kind === 'event') ui.logMark = run.pending.log.length;
    if (d.actor) {
      const [opt, who] = d.actor.split('|') as [string, string];
      chooseOption(run, opt, who);
    } else if (d.opt) chooseOption(run, d.opt);
    else if (d.buy !== undefined) buy(run, Number(d.buy));
    else if (d.send !== undefined) sendOnErrand(run, d.send || null);
    else if (d.release) {
      // F33: Der Name aus dem Feld gilt, bevor der Neue an Bord kommt
      const c = (run.pending as { candidate: RunFriend }).candidate;
      c.name = val('recruit-name').trim().slice(0, 24) || c.name;
      acceptRecruit(run, d.release);
    } else if (d.artifact) chooseArtifact(run, d.artifact);
    else if (d.level) {
      const f = ui.dialog?.kind === 'levelup' ? run.crew.find((x) => x.id === (ui.dialog as { id: string }).id) : run.crew.find((x) => x.pendingLevels > 0 && !ui.laterLevel.has(x.id));
      if (f) {
        chooseLevel(run, f.id, d.level);
        if (f.pendingLevels <= 0 || !levelChoices(f).length) ui.dialog = null;
      }
    } else if (d.equip) {
      const id = (ui.dialog as { id: string }).id;
      equipItem(run, id, d.equip);
    } else if (d.use) {
      useItem(run, (ui.dialog as { id: string }).id, d.use);
    } else if (d.give) {
      const item = (ui.dialog as { item: string }).item;
      if (getItem(item).kind === 'equip') equipItem(run, d.give, item);
      else useItem(run, d.give, item);
      ui.dialog = null;
    } else
      switch (d.act) {
        case 'leave':
          leaveShore(run);
          break;
        case 'retry':
          if (run.pending.kind === 'event') ui.logMark = run.pending.log.length;
          tryAttempt(run);
          break;
        case 'giveup':
          if (run.pending.kind === 'event') ui.logMark = run.pending.log.length;
          giveUpAttempt(run);
          break;
        case 'accept': {
          const c = (run.pending as { candidate: RunFriend }).candidate;
          c.name = val('recruit-name').trim().slice(0, 24) || c.name;
          acceptRecruit(run);
          break;
        }
        case 'decline':
          declineRecruit(run);
          break;
        case 'morning':
          startNextDay(run);
          break;
        case 'report':
          dismissReport(run);
          break;
        case 'named':
          for (const input of card.querySelectorAll<HTMLInputElement>('[data-name]')) renameFriend(run, input.dataset.name!, input.value);
          ui.dialog = null;
          break;
        case 'honor':
          answerHonor(run, val('honor-title'));
          break;
        case 'nohonor':
          answerHonor(run, null);
          break;
        case 'later': {
          const f = run.crew.find((x) => x.pendingLevels > 0 && !ui.laterLevel.has(x.id));
          if (ui.dialog?.kind === 'levelup') ui.laterLevel.add(ui.dialog.id);
          else if (f) ui.laterLevel.add(f.id);
          ui.dialog = null;
          break;
        }
        case 'levelnow':
          if (ui.dialog?.kind === 'friend') ui.dialog = { kind: 'levelup', id: ui.dialog.id };
          break;
        case 'close':
          if (ui.dialog?.kind === 'friend') renameFriend(run, ui.dialog.id, val('friend-name'));
          ui.dialog = null;
          break;
        case 'title':
          ui.dialog = null;
          app.toTitle();
          return;
        default:
          return;
      }
    app.sync();
  };
  // Der getippte Name bleibt, auch wenn man zwischendurch einen Freund ansieht
  const recruitName = document.getElementById('recruit-name') as HTMLInputElement | null;
  if (recruitName) {
    recruitName.oninput = () => {
      const p = run.pending;
      const c = p.kind === 'recruit' ? p.candidate : p.kind === 'event' ? p.subject : undefined;
      const name = recruitName.value.trim().slice(0, 24);
      if (c && name) c.name = name;
    };
  }
  // Spieltest Phase 4 (Fabi): Daneben klicken schließt Freund-, Item- und Menüfenster, wie „Fertig“ oder „Abbrechen“
  $('dialog').onclick = (ev) => {
    if (ev.target === ev.currentTarget) dialogBack(app);
  };
  for (const input of card.querySelectorAll<HTMLInputElement>('input')) {
    input.onkeydown = (ev) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        (card.querySelector('button.primary') as HTMLButtonElement | null)?.click();
      }
    };
  }
}

/** B im Dialog: schließen, wo das geht */
export function dialogBack(app: RunApp): boolean {
  if (!ui.dialog) return false;
  if (ui.dialog.kind === 'friend') renameFriend(app.run, ui.dialog.id, (document.getElementById('friend-name') as HTMLInputElement | null)?.value ?? '');
  if (ui.dialog.kind === 'naming') return false;
  if (ui.dialog.kind === 'levelup') ui.laterLevel.add(ui.dialog.id);
  ui.dialog = null;
  app.sync();
  return true;
}

/** Nach jeder Run-Änderung vergessen wir verschobene Level-Ups nicht, aber neue Level fragen wieder */
export function resetLater(): void {
  ui.laterLevel.clear();
}

export function hasUiDialog(): boolean {
  return ui.dialog !== null;
}

// ---------- Chronik und Run-Übersicht ----------

export function chronicleHtml(run: RunState, final = false): string {
  const days = [...new Set(run.chronicle.map((c) => c.day))];
  const body = days
    .map((d) => {
      const items = run.chronicle
        .filter((c) => c.day === d)
        .map((c) => {
          const nums = c.friends?.length
            ? `<div class="nums">${c.friends.map((f) => `${esc(f.name)}: ${f.farewell ? 'Abschied' : `Schaden ${f.damage}, eingesteckt ${f.taken}${f.healed ? `, geheilt ${f.healed}` : ''}, +${f.xp} XP${f.levelUp ? ', Level-Up' : ''}`}`).join(' · ')}</div>`
            : '';
          return `<li><span class="when">Halt ${c.landing} · ${esc(stageName(c.stage))}</span><span class="what">${esc(c.title)}</span> ${esc(c.line)}${nums}</li>`;
        })
        .join('');
      return `<h3>Tag ${d}</h3><ul class="chron">${items}</ul>`;
    })
    .join('');
  return `<h2>Chronik</h2><p class="hint">Nach jedem Halt ein Eintrag. Die Stimmen der Käpt'ns kommen mit dem Boot.</p>${body}
    ${final ? '' : '<div class="actions"><button class="primary" id="chron-back">Zurück zum Fluss <kbd class="pad">B</kbd></button></div>'}`;
}

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * M9 (Fabi, 29.10.2026): Am Ende eines Runs eine freundliche Seite: drei Fakten über die Reise, ein Satz fürs Logbuch,
 * den man ändern darf, und die Bibliothek aller Runs mit Vergleichen. Darunter wie bisher alle Freunde und die Chronik.
 */
export function endHtml(run: RunState, library: RunRecord[]): string {
  const won = run.status === 'won';
  const rec = library.find((r) => r.id === String(run.seed)) ?? runRecord(run);
  const facts = runFacts(run);
  const compare = compareRuns(rec, library);
  const all = [...run.crew, ...run.gone];
  const rows = all
    .map((f) => {
      const t = f.tally;
      const status = f.gone
        ? `<em class="warn">${f.gone.how === 'abschied' ? 'Abschied' : f.gone.how === 'zurueckgelassen' ? 'zurückgelassen' : 'weggeblieben'}</em><small>${esc(f.gone.text)}</small>`
        : '<span class="ok">an Bord</span>';
      return `<tr class="${f.gone ? 'gone' : ''}">
        <th scope="row"><span class="dot" style="background:${CLASS_COLORS[f.friendClass]}"></span>${esc(displayName(f))}<small>${esc(getClass(f.friendClass).name)}${f.titles.length ? ` · ${f.titles.map(esc).join(', ')}` : ''}</small></th>
        <td>${f.level}</td><td class="status">${status}</td><td>${t.fights}</td><td>${Math.round(t.damage)}</td><td>${Math.round(t.taken)}</td><td>${Math.round(t.healed)}</td><td>${t.kills}</td><td>${t.xp}</td>
      </tr>`;
    })
    .join('');
  // Runde 7: mit den Bildern des Grafik-Leads die Ankunft am Meer, die Logbuch-Seite und das Regal der Bibliothek
  const n = library.findIndex((r) => r.id === rec.id) + 1;
  return `<h2>${won ? 'Ziel erreicht' : 'Die Reise ist zu Ende'}</h2>
    ${won ? '<div class="arrival"><canvas id="end-arrival" width="640" height="360" aria-label="Das Boot kommt am Meer an"></canvas></div>' : ''}
    <p class="hint">${won ? `Nach ${run.day} Tagen liegt der Sturm hinter euch.` : 'Niemand ist mehr an Bord.'} ${count(run.chronicle.filter((c) => c.title.includes(':')).length, 'Kampf', 'Kämpfe')}, ${count(run.gone.filter((f) => f.gone?.how === 'abschied').length, 'Abschied', 'Abschiede')}.</p>
    <div class="end-books">
      <section class="end-log">${logbookHtml(`${n ? `Reise ${n}, ` : ''}${rec.date || 'heute'}`, rec.line, facts)}
        <small class="hint">Das Spiel schlägt einen Satz vor; ihr dürft ihn ändern. So steht die Reise in der Bibliothek.</small>
      </section>
      <section class="end-lib"><h3>Bibliothek</h3>
        <div id="end-regal">${regalHtml(library, rec.id)}</div>
        <ol class="shelf book-detail" id="book-detail">${recordHtml(rec, rec.id, n || undefined)}</ol>
        <ul class="compare">${compare.map((c) => `<li>${esc(c)}</li>`).join('')}</ul>
      </section>
    </div>
    <h3>Alle Freunde dieser Reise</h3>
    <div class="table-wrap"><table>
      <thead><tr><th>Freund</th><th>Level</th><th style="text-align:left">Am Ende</th><th>Kämpfe</th><th>Schaden</th><th>Eingesteckt</th><th>Geheilt</th><th>Besiegt</th><th>XP</th></tr></thead>
      <tbody>${rows}</tbody></table></div>
    <div class="actions"><button class="primary" id="end-new">Neuer Run</button><button id="end-title">Zum Titel</button></div>
    ${chronicleHtml(run, true).replace('<h2>Chronik</h2>', '<h3>Chronik</h3>')}`;
}
